import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumCurrency } from "./enums/enumCurrency.generated";
import { EnumDiscountBasis } from "./enums/enumDiscountBasis.generated";
import { EnumDistributionType } from "./enums/enumDistributionType.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumItemMainPurpose } from "./enums/enumItemMainPurpose.generated";
import { EnumNatureOfTransactionCode } from "./enums/enumNatureOfTransactionCode.generated";
import { EnumOrderSource } from "./enums/enumOrderSource.generated";
import { EnumOrderStatus } from "./enums/enumOrderStatus.generated";
import { EnumRetailPaymentMethod } from "./enums/enumRetailPaymentMethod.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";
import { EnumStockMovementReferenceSource } from "./enums/enumStockMovementReferenceSource.generated";

// Model Objects

export class OrderSummary extends ModelObject<OrderSummary> {

    /**
    * Timestamp at which the order was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    public id: Guid = Guid.createEmpty();
    /**
    * Order Number. Assigned at order creation. Not editable.
    * Not Nullable
    */
    public orderNumber: string = "";
    /**
    * Outlet which the order is for. Shown on docs. Used to determine which contact in accounts the order is sent to (if not invoiced to group). Outlet is required at order create and then Not editable.
    */
    public outletId: Guid = Guid.createEmpty();
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the order was cancelled. Captured by system not user editable. (Null if not cancelled).
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    /**
    * Date the order was/will be despatched for delivery. This is also the date at which any duty liability for the order occurs.
    */
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Date the order will be / was Delivered. Sent to accounts as the Invoice Date for the order.
    */
    public deliveryDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Processing Status Of the order. Not directly editable, but updated as explicit state change operations (complete/cancel/archive) are invoked. Orders are created with Status Open. : 0 = None, 1 = Open, 2 = Complete, 3 = Historic, 4 = Cancelled, 5 = Posting, 6 = Draft
    */
    public status: EnumOrderStatus = EnumOrderStatus.none;
    /**
    * Whether an invoice has been sent for this order. Just for user information. 
    */
    public invoiceSent: boolean = false;
    /**
    * The email address invoices will be sent to. Shown on docs. Used when invoices auto-emailed. Copied from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceEmailAddress: string = "";
    /**
    * Invoice Number. Assigned on accounts posting or invoice document generation. InvoiceNumber is the key for sending the invoice to accounts packages. Not user editable. 
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceNumber: string = "";
    /**
    * Customer Reference. Displays on documents, sent to accounts packages.
    * Not Nullable
    * Max Length : 1000
    */
    public customerReference: string = "";
    /**
    * Our Reference. Just for user information.
    * Not Nullable
    * Max Length : 1000
    */
    public ourReference: string = "";
    /**
    * User who initially created the order. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Delivery Address Line 1. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress1: string = "";
    /**
    * Delivery Address Line 2. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress2: string = "";
    /**
    * Delivery Address Line 3. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress3: string = "";
    /**
    * Delivery Address Line 4. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress4: string = "";
    /**
    * Delivery Address Line 5 - County. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress5: string = "";
    /**
    * Delivery Address Line 6 - Country. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress6: string = "";
    /**
    * Delivery Post Code. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryPostcode: string = "";
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area. Displays in Delivery App. Copies from Outlet by default.
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    /**
    * Order Source. Assigned at order creation. Not editable. : 0 = None, 1 = BrewMan, 2 = WooCommerce, 3 = Shopify, 4 = Sellar, 5 = Zettle, 6 = Other, 7 = EposNow, 8 = Beerflex, 9 = Square, 10 = Squarespace, 11 = Ecwid, 12 = EeBria, 13 = Commerce7, 14 = TradePortal, 15 = Amazon, 16 = BigCommerce, 17 = GoTab
    */
    public orderSource: EnumOrderSource = EnumOrderSource.none;
    /**
    * Reference of Order in External system. (OrderSource identifies which system the order was imported from eg WooCommerce / Shopify). Not editable.
    * Not Nullable
    * Max Length : 1000
    */
    public externalReference: string = "";
    /**
    * Timestamp at which the order was last updated on the system. Captured by system not user editable.
    */
    public lastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Total Net Price of Order. Essentially a cached value of the sum of NetPrice of lines. Updated when lines changed. Not editable.
    */
    public totalNetPrice: number = 0;
    /**
    * Total VAT of Order. Essentially a cached value of the sum of Vat of lines. Updated when lines changed. Not editable.
    */
    public totalVat: number = 0;
    /**
    * Reason Order Cancelled. Expected to be provided at time of cancelling an order.
    * Not Nullable
    * Max Length : 100000
    */
    public cancellationReason: string = "";
    /**
    * Distribution Type. Drives the manner of distribution, particularly type Delivery which is included in the distribution planning and delivery app. Copied from Outlet by default. : 0 = None, 1 = Delivery, 2 = Courier, 3 = Collection
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    /**
    * Private notes relating to dispatch of this order. Not displayed to customer. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 100000
    */
    public internalDispatchNotes: string = "";
    /**
    * The phone number to use for delivery issues and updates. Just for user information. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryTelephoneNumber: string = "";
    /**
    * Order Type. Determines the duty implication of the order. Affects weight allocated to lines (export/normal). Can be changed, but all Order Lines should be removed if StandardOrderType is changed. : 0 = FullPriceDutyPaid, 1 = PriceExcludingDutyDutySuspended, 2 = FullPriceDutyPaidElsewhere, 3 = ExportDutySuspended, 4 = ConsumeOnPremisesNoDuty
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    /**
    * User notes relating to dispatch of this order. Displays on documents. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 100000
    */
    public externalDispatchNotes: string = "";
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    private _courierId: Guid | null = null;
    /**
    * Courier. Defaults to Courier chosen for relevant Outlet. Required if distribution type is Courier.
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    /**
    * Reference name or number from the Accounts Link posting.
    * Not Nullable
    * Max Length : 1000
    */
    public accountsLinkReferenceName: string = "";
    /**
    * Delivery Recipient Name. Just for user information. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryRecipient: string = "";
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    private _validUntilDate: LocalDate | null = null;
    /**
    * Date until which a draft order is valid.
    */
    public get validUntilDate(): LocalDate | null { return this._validUntilDate }
    public set validUntilDate(value: LocalDate | null) {
      this._validUntilDate = value === undefined ? null : value;
    }
    /**
    * Whether an invoice has been printed or downloaded for this order. Just for user information. 
    */
    public invoicePrinted: boolean = false;
    /**
    * Total Regional Tax of Order.  Updated when lines changed. Not editable.
    */
    public totalRegionalTax: number = 0;
    /**
    * The ISO 4217 Currency Code of this order.
    */
    public currencyCode: EnumCurrency = EnumCurrency.none;
    /**
    * Whether a dispatch note has been printed or downloaded for this order. Just for user information. 
    */
    public dispatchNotePrinted: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.orderNumber = jsonSerializedObject.orderNumber;
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.deliveryDate = LocalDate.parse(jsonSerializedObject.deliveryDate);
          this.status = EnumOrderStatus.getByValue(jsonSerializedObject.status, this.status);
          this.invoiceSent = jsonSerializedObject.invoiceSent;
          this.invoiceEmailAddress = jsonSerializedObject.invoiceEmailAddress;
          this.invoiceNumber = jsonSerializedObject.invoiceNumber;
          this.customerReference = jsonSerializedObject.customerReference;
          this.ourReference = jsonSerializedObject.ourReference;
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.deliveryAddress1 = jsonSerializedObject.deliveryAddress1;
          this.deliveryAddress2 = jsonSerializedObject.deliveryAddress2;
          this.deliveryAddress3 = jsonSerializedObject.deliveryAddress3;
          this.deliveryAddress4 = jsonSerializedObject.deliveryAddress4;
          this.deliveryAddress5 = jsonSerializedObject.deliveryAddress5;
          this.deliveryAddress6 = jsonSerializedObject.deliveryAddress6;
          this.deliveryPostcode = jsonSerializedObject.deliveryPostcode;
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.orderSource = EnumOrderSource.getByValue(jsonSerializedObject.orderSource, this.orderSource);
          this.externalReference = jsonSerializedObject.externalReference;
          this.lastUpdatedAt = Instant.parse(jsonSerializedObject.lastUpdatedAt);
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.totalVat = jsonSerializedObject.totalVat;
          this.cancellationReason = jsonSerializedObject.cancellationReason;
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.internalDispatchNotes = jsonSerializedObject.internalDispatchNotes;
          this.deliveryTelephoneNumber = jsonSerializedObject.deliveryTelephoneNumber;
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.externalDispatchNotes = jsonSerializedObject.externalDispatchNotes;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.accountsLinkReferenceName = jsonSerializedObject.accountsLinkReferenceName;
          this.deliveryRecipient = jsonSerializedObject.deliveryRecipient;
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.validUntilDate = jsonSerializedObject.validUntilDate == null ? null : LocalDate.parse(jsonSerializedObject.validUntilDate);
          this.invoicePrinted = jsonSerializedObject.invoicePrinted;
          this.totalRegionalTax = jsonSerializedObject.totalRegionalTax;
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
          this.dispatchNotePrinted = jsonSerializedObject.dispatchNotePrinted;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.id = this.id.toString();
        toRet.orderNumber = this.orderNumber;
        toRet.outletId = this.outletId.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.despatchDate = this.despatchDate.toString();
        toRet.deliveryDate = this.deliveryDate.toString();
        toRet.status = this.status.value;
        toRet.invoiceSent = this.invoiceSent;
        toRet.invoiceEmailAddress = this.invoiceEmailAddress;
        toRet.invoiceNumber = this.invoiceNumber;
        toRet.customerReference = this.customerReference;
        toRet.ourReference = this.ourReference;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.deliveryAddress1 = this.deliveryAddress1;
        toRet.deliveryAddress2 = this.deliveryAddress2;
        toRet.deliveryAddress3 = this.deliveryAddress3;
        toRet.deliveryAddress4 = this.deliveryAddress4;
        toRet.deliveryAddress5 = this.deliveryAddress5;
        toRet.deliveryAddress6 = this.deliveryAddress6;
        toRet.deliveryPostcode = this.deliveryPostcode;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.orderSource = this.orderSource.value;
        toRet.externalReference = this.externalReference;
        toRet.lastUpdatedAt = this.lastUpdatedAt.toString();
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.totalVat = this.totalVat;
        toRet.cancellationReason = this.cancellationReason;
        toRet.distributionType = this.distributionType.value;
        toRet.internalDispatchNotes = this.internalDispatchNotes;
        toRet.deliveryTelephoneNumber = this.deliveryTelephoneNumber;
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.externalDispatchNotes = this.externalDispatchNotes;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.accountsLinkReferenceName = this.accountsLinkReferenceName;
        toRet.deliveryRecipient = this.deliveryRecipient;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.validUntilDate = this.validUntilDate == null ? null : this.validUntilDate.toString();
        toRet.invoicePrinted = this.invoicePrinted;
        toRet.totalRegionalTax = this.totalRegionalTax;
        toRet.currencyCode = this.currencyCode.value;
        toRet.dispatchNotePrinted = this.dispatchNotePrinted;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "OrderSummary.Id cannot be empty"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OrderSummary.OutletId cannot be empty"},
            {field: "invoiceEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceEmailAddress has too many characters (1000)"},
            {field: "invoiceNumber", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceNumber has too many characters (1000)"},
            {field: "customerReference", rule: (v: any) => v == null || v.length <= 1000 || "CustomerReference has too many characters (1000)"},
            {field: "ourReference", rule: (v: any) => v == null || v.length <= 1000 || "OurReference has too many characters (1000)"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "OrderSummary.CreatedByUserId cannot be empty"},
            {field: "deliveryAddress1", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress1 has too many characters (1000)"},
            {field: "deliveryAddress2", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress2 has too many characters (1000)"},
            {field: "deliveryAddress3", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress3 has too many characters (1000)"},
            {field: "deliveryAddress4", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress4 has too many characters (1000)"},
            {field: "deliveryAddress5", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress5 has too many characters (1000)"},
            {field: "deliveryAddress6", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress6 has too many characters (1000)"},
            {field: "deliveryPostcode", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryPostcode has too many characters (1000)"},
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "OrderSummary.DeliveryAreaId cannot be empty"},
            {field: "externalReference", rule: (v: any) => v == null || v.length <= 1000 || "ExternalReference has too many characters (1000)"},
            {field: "cancellationReason", rule: (v: any) => v == null || v.length <= 100000 || "CancellationReason has too many characters (100000)"},
            {field: "internalDispatchNotes", rule: (v: any) => v == null || v.length <= 100000 || "InternalDispatchNotes has too many characters (100000)"},
            {field: "deliveryTelephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryTelephoneNumber has too many characters (1000)"},
            {field: "externalDispatchNotes", rule: (v: any) => v == null || v.length <= 100000 || "ExternalDispatchNotes has too many characters (100000)"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "OrderSummary.CourierId cannot be empty"},
            {field: "accountsLinkReferenceName", rule: (v: any) => v == null || v.length <= 1000 || "AccountsLinkReferenceName has too many characters (1000)"},
            {field: "deliveryRecipient", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryRecipient has too many characters (1000)"},
    ];
}


export class OrderSummariesResult extends ModelObject<OrderSummariesResult> {

    public matchingCount: number = 0;
    public orders: (OrderSummary)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.orders = jsonSerializedObject.orders.map((v: any) => new OrderSummary(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.orders = this.orders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "orders", rule: (v: any) => !!v || "orders is required"},
            {field: "orders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OrderSummary[]).reduce((acc: Array<string>, curr: OrderSummary) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class OrderFilter extends ModelObject<OrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _singleOutletId: Guid | null = null;
    public get singleOutletId(): Guid | null { return this._singleOutletId }
    public set singleOutletId(value: Guid | null) {
      this._singleOutletId = value === undefined ? null : value;
    }
    private _singleGroupId: Guid | null = null;
    public get singleGroupId(): Guid | null { return this._singleGroupId }
    public set singleGroupId(value: Guid | null) {
      this._singleGroupId = value === undefined ? null : value;
    }
    private _fromDeliveryDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) delivery date
    */
    public get fromDeliveryDate(): LocalDate | null { return this._fromDeliveryDate }
    public set fromDeliveryDate(value: LocalDate | null) {
      this._fromDeliveryDate = value === undefined ? null : value;
    }
    private _toDeliveryDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) delivery date
    */
    public get toDeliveryDate(): LocalDate | null { return this._toDeliveryDate }
    public set toDeliveryDate(value: LocalDate | null) {
      this._toDeliveryDate = value === undefined ? null : value;
    }
    private _fromDutyReturnDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) effective duty return date. (Usually DespatchDate but possibly overriden by DutyReturnDateOverride).
    */
    public get fromDutyReturnDate(): LocalDate | null { return this._fromDutyReturnDate }
    public set fromDutyReturnDate(value: LocalDate | null) {
      this._fromDutyReturnDate = value === undefined ? null : value;
    }
    private _toDutyReturnDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) effective duty return date. (Usually DespatchDate but possibly overriden by DutyReturnDateOverride).
    */
    public get toDutyReturnDate(): LocalDate | null { return this._toDutyReturnDate }
    public set toDutyReturnDate(value: LocalDate | null) {
      this._toDutyReturnDate = value === undefined ? null : value;
    }
    private _fromDespatchDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) despatch date
    */
    public get fromDespatchDate(): LocalDate | null { return this._fromDespatchDate }
    public set fromDespatchDate(value: LocalDate | null) {
      this._fromDespatchDate = value === undefined ? null : value;
    }
    private _toDespatchDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) despatch date
    */
    public get toDespatchDate(): LocalDate | null { return this._toDespatchDate }
    public set toDespatchDate(value: LocalDate | null) {
      this._toDespatchDate = value === undefined ? null : value;
    }
    private _sources: readonly (EnumOrderSource)[] | null = null;
    public get sources(): readonly (EnumOrderSource)[] | null { return this._sources }
    public set sources(value: readonly (EnumOrderSource)[] | null) {
      this._sources = value === undefined ? null : value;
    }
    private _distributionTypes: readonly (EnumDistributionType)[] | null = null;
    public get distributionTypes(): readonly (EnumDistributionType)[] | null { return this._distributionTypes }
    public set distributionTypes(value: readonly (EnumDistributionType)[] | null) {
      this._distributionTypes = value === undefined ? null : value;
    }
    private _limitCourierIdsForCourierOrders: readonly (Guid)[] | null = null;
    public get limitCourierIdsForCourierOrders(): readonly (Guid)[] | null { return this._limitCourierIdsForCourierOrders }
    public set limitCourierIdsForCourierOrders(value: readonly (Guid)[] | null) {
      this._limitCourierIdsForCourierOrders = value === undefined ? null : value;
    }
    private _externalReferences: readonly (string)[] | null = null;
    public get externalReferences(): readonly (string)[] | null { return this._externalReferences }
    public set externalReferences(value: readonly (string)[] | null) {
      this._externalReferences = value === undefined ? null : value;
    }
    private _orderNumbers: readonly (string)[] | null = null;
    public get orderNumbers(): readonly (string)[] | null { return this._orderNumbers }
    public set orderNumbers(value: readonly (string)[] | null) {
      this._orderNumbers = value === undefined ? null : value;
    }
    private _invoiceNumbers: readonly (string)[] | null = null;
    public get invoiceNumbers(): readonly (string)[] | null { return this._invoiceNumbers }
    public set invoiceNumbers(value: readonly (string)[] | null) {
      this._invoiceNumbers = value === undefined ? null : value;
    }
    private _limitStatuses: readonly (EnumOrderStatus)[] | null = null;
    /**
    * If provided only matching statuses are returned
    */
    public get limitStatuses(): readonly (EnumOrderStatus)[] | null { return this._limitStatuses }
    public set limitStatuses(value: readonly (EnumOrderStatus)[] | null) {
      this._limitStatuses = value === undefined ? null : value;
    }
    private _limitOrderHeaderIds: readonly (Guid)[] | null = null;
    public get limitOrderHeaderIds(): readonly (Guid)[] | null { return this._limitOrderHeaderIds }
    public set limitOrderHeaderIds(value: readonly (Guid)[] | null) {
      this._limitOrderHeaderIds = value === undefined ? null : value;
    }
    private _standardOrderTypes: readonly (EnumStandardOrder)[] | null = null;
    public get standardOrderTypes(): readonly (EnumStandardOrder)[] | null { return this._standardOrderTypes }
    public set standardOrderTypes(value: readonly (EnumStandardOrder)[] | null) {
      this._standardOrderTypes = value === undefined ? null : value;
    }
    private _fromCancelledAt: Instant | null = null;
    public get fromCancelledAt(): Instant | null { return this._fromCancelledAt }
    public set fromCancelledAt(value: Instant | null) {
      this._fromCancelledAt = value === undefined ? null : value;
    }
    private _toCancelledAt: Instant | null = null;
    public get toCancelledAt(): Instant | null { return this._toCancelledAt }
    public set toCancelledAt(value: Instant | null) {
      this._toCancelledAt = value === undefined ? null : value;
    }
    private _fromCreatedAt: Instant | null = null;
    public get fromCreatedAt(): Instant | null { return this._fromCreatedAt }
    public set fromCreatedAt(value: Instant | null) {
      this._fromCreatedAt = value === undefined ? null : value;
    }
    private _toCreatedAt: Instant | null = null;
    public get toCreatedAt(): Instant | null { return this._toCreatedAt }
    public set toCreatedAt(value: Instant | null) {
      this._toCreatedAt = value === undefined ? null : value;
    }
    private _fromLastUpdatedAt: Instant | null = null;
    /**
    * Minimum (inclusive) last updated at.
    */
    public get fromLastUpdatedAt(): Instant | null { return this._fromLastUpdatedAt }
    public set fromLastUpdatedAt(value: Instant | null) {
      this._fromLastUpdatedAt = value === undefined ? null : value;
    }
    private _toLastUpdatedAt: Instant | null = null;
    /**
    * Maximum (inclusive) last updated at.
    */
    public get toLastUpdatedAt(): Instant | null { return this._toLastUpdatedAt }
    public set toLastUpdatedAt(value: Instant | null) {
      this._toLastUpdatedAt = value === undefined ? null : value;
    }
    private _outletIds: readonly (Guid)[] | null = null;
    public get outletIds(): readonly (Guid)[] | null { return this._outletIds }
    public set outletIds(value: readonly (Guid)[] | null) {
      this._outletIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.singleOutletId = jsonSerializedObject.singleOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.singleOutletId);
          this.singleGroupId = jsonSerializedObject.singleGroupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.singleGroupId);
          this.fromDeliveryDate = jsonSerializedObject.fromDeliveryDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDeliveryDate);
          this.toDeliveryDate = jsonSerializedObject.toDeliveryDate == null ? null : LocalDate.parse(jsonSerializedObject.toDeliveryDate);
          this.fromDutyReturnDate = jsonSerializedObject.fromDutyReturnDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDutyReturnDate);
          this.toDutyReturnDate = jsonSerializedObject.toDutyReturnDate == null ? null : LocalDate.parse(jsonSerializedObject.toDutyReturnDate);
          this.fromDespatchDate = jsonSerializedObject.fromDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDespatchDate);
          this.toDespatchDate = jsonSerializedObject.toDespatchDate == null ? null : LocalDate.parse(jsonSerializedObject.toDespatchDate);
          this.sources = jsonSerializedObject.sources == null ? null : jsonSerializedObject.sources.map((v: any) => EnumOrderSource.getByValue(v));
          this.distributionTypes = jsonSerializedObject.distributionTypes == null ? null : jsonSerializedObject.distributionTypes.map((v: any) => EnumDistributionType.getByValue(v));
          this.limitCourierIdsForCourierOrders = jsonSerializedObject.limitCourierIdsForCourierOrders == null ? null : jsonSerializedObject.limitCourierIdsForCourierOrders.map((v: any) => Guid.parseOrEmpty(v));
          this.externalReferences = jsonSerializedObject.externalReferences == null ? null : jsonSerializedObject.externalReferences.map((v: any) => v);
          this.orderNumbers = jsonSerializedObject.orderNumbers == null ? null : jsonSerializedObject.orderNumbers.map((v: any) => v);
          this.invoiceNumbers = jsonSerializedObject.invoiceNumbers == null ? null : jsonSerializedObject.invoiceNumbers.map((v: any) => v);
          this.limitStatuses = jsonSerializedObject.limitStatuses == null ? null : jsonSerializedObject.limitStatuses.map((v: any) => EnumOrderStatus.getByValue(v));
          this.limitOrderHeaderIds = jsonSerializedObject.limitOrderHeaderIds == null ? null : jsonSerializedObject.limitOrderHeaderIds.map((v: any) => Guid.parseOrEmpty(v));
          this.standardOrderTypes = jsonSerializedObject.standardOrderTypes == null ? null : jsonSerializedObject.standardOrderTypes.map((v: any) => EnumStandardOrder.getByValue(v));
          this.fromCancelledAt = jsonSerializedObject.fromCancelledAt == null ? null : Instant.parse(jsonSerializedObject.fromCancelledAt);
          this.toCancelledAt = jsonSerializedObject.toCancelledAt == null ? null : Instant.parse(jsonSerializedObject.toCancelledAt);
          this.fromCreatedAt = jsonSerializedObject.fromCreatedAt == null ? null : Instant.parse(jsonSerializedObject.fromCreatedAt);
          this.toCreatedAt = jsonSerializedObject.toCreatedAt == null ? null : Instant.parse(jsonSerializedObject.toCreatedAt);
          this.fromLastUpdatedAt = jsonSerializedObject.fromLastUpdatedAt == null ? null : Instant.parse(jsonSerializedObject.fromLastUpdatedAt);
          this.toLastUpdatedAt = jsonSerializedObject.toLastUpdatedAt == null ? null : Instant.parse(jsonSerializedObject.toLastUpdatedAt);
          this.outletIds = jsonSerializedObject.outletIds == null ? null : jsonSerializedObject.outletIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.singleOutletId = this.singleOutletId == null ? null : this.singleOutletId.toString();
        toRet.singleGroupId = this.singleGroupId == null ? null : this.singleGroupId.toString();
        toRet.fromDeliveryDate = this.fromDeliveryDate == null ? null : this.fromDeliveryDate.toString();
        toRet.toDeliveryDate = this.toDeliveryDate == null ? null : this.toDeliveryDate.toString();
        toRet.fromDutyReturnDate = this.fromDutyReturnDate == null ? null : this.fromDutyReturnDate.toString();
        toRet.toDutyReturnDate = this.toDutyReturnDate == null ? null : this.toDutyReturnDate.toString();
        toRet.fromDespatchDate = this.fromDespatchDate == null ? null : this.fromDespatchDate.toString();
        toRet.toDespatchDate = this.toDespatchDate == null ? null : this.toDespatchDate.toString();
        toRet.sources = this.sources == null ? null : this.sources.map(v => v.value);
        toRet.distributionTypes = this.distributionTypes == null ? null : this.distributionTypes.map(v => v.value);
        toRet.limitCourierIdsForCourierOrders = this.limitCourierIdsForCourierOrders == null ? null : this.limitCourierIdsForCourierOrders.map(v => v.toString());
        toRet.externalReferences = this.externalReferences == null ? null : this.externalReferences.map(v => v);
        toRet.orderNumbers = this.orderNumbers == null ? null : this.orderNumbers.map(v => v);
        toRet.invoiceNumbers = this.invoiceNumbers == null ? null : this.invoiceNumbers.map(v => v);
        toRet.limitStatuses = this.limitStatuses == null ? null : this.limitStatuses.map(v => v.value);
        toRet.limitOrderHeaderIds = this.limitOrderHeaderIds == null ? null : this.limitOrderHeaderIds.map(v => v.toString());
        toRet.standardOrderTypes = this.standardOrderTypes == null ? null : this.standardOrderTypes.map(v => v.value);
        toRet.fromCancelledAt = this.fromCancelledAt == null ? null : this.fromCancelledAt.toString();
        toRet.toCancelledAt = this.toCancelledAt == null ? null : this.toCancelledAt.toString();
        toRet.fromCreatedAt = this.fromCreatedAt == null ? null : this.fromCreatedAt.toString();
        toRet.toCreatedAt = this.toCreatedAt == null ? null : this.toCreatedAt.toString();
        toRet.fromLastUpdatedAt = this.fromLastUpdatedAt == null ? null : this.fromLastUpdatedAt.toString();
        toRet.toLastUpdatedAt = this.toLastUpdatedAt == null ? null : this.toLastUpdatedAt.toString();
        toRet.outletIds = this.outletIds == null ? null : this.outletIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "singleOutletId", rule: (v: any) => v == null || !v.isEmpty() || "OrderFilter.SingleOutletId cannot be empty"},
            {field: "singleGroupId", rule: (v: any) => v == null || !v.isEmpty() || "OrderFilter.SingleGroupId cannot be empty"},
    ];
}


export class OrderHeaderReadOnly extends ModelObject<OrderHeaderReadOnly> {

    /**
    * Timestamp at which the order was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Timestamp at which the order was last updated on the system. Captured by system not user editable.
    */
    public lastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Reason Order Cancelled. Expected to be provided at time of cancelling an order.
    * Not Nullable
    * Max Length : 100000
    */
    public cancellationReason: string = "";
    private _cancelledByUserId: Guid | null = null;
    /**
    * User who cancelled the order. Captured by system not user editable. (Null if not cancelled).
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the order was cancelled. Captured by system not user editable. (Null if not cancelled).
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    /**
    * Credit Terms Description. Frozen at order entry so historic invoices can print original details. Copied from Outlets Credit terms at order creation and not editable.
    * Not Nullable
    * Max Length : 1000
    */
    public creditTermsDescription: string = "";
    private _deliveryLatitude: number | null = null;
    /**
    * Latitude of delviery address (if known). Used for distribution planning, routing, mapping. Copies from Outlet by default. Not directly editable by user, but updated automatically when order delivery address changed.
    */
    public get deliveryLatitude(): number | null { return this._deliveryLatitude }
    public set deliveryLatitude(value: number | null) {
      this._deliveryLatitude = value === undefined ? null : value;
    }
    private _deliveryLongitude: number | null = null;
    /**
    * Longitude of delviery address (if known). Used for distribution planning, routing, mapping. Copies from Outlet by default. Not directly editable by user, but updated automatically when order delivery address changed.
    */
    public get deliveryLongitude(): number | null { return this._deliveryLongitude }
    public set deliveryLongitude(value: number | null) {
      this._deliveryLongitude = value === undefined ? null : value;
    }
    /**
    * Reference of Order in External system. (OrderSource identifies which system the order was imported from eg WooCommerce / Shopify). Not editable.
    * Not Nullable
    * Max Length : 1000
    */
    public externalReference: string = "";
    private _groupId: Guid | null = null;
    /**
    * Group (if any). Affects Discounts. Used for accounts posting (invoices post to the group not outlet unless group is marked as SendInvoicesToOutlet). Captured from outlet at order creation. Not editable.
    */
    public get groupId(): Guid | null { return this._groupId }
    public set groupId(value: Guid | null) {
      this._groupId = value === undefined ? null : value;
    }
    /**
    * Invoice Number. Assigned on accounts posting or invoice document generation. InvoiceNumber is the key for sending the invoice to accounts packages. Not user editable. 
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceNumber: string = "";
    /**
    * Identified the order as a Retail Order. Retail orders are not sent to accounts individually, but instead are packaged (typically daily) into a single summary which is sent to accounts. Retail orders present differently to Trade on UI. Captured from outlet at order creation. Not editable.
    */
    public isRetailOrder: boolean = false;
    /**
    * Order Number. Assigned at order creation. Not editable.
    * Not Nullable
    */
    public orderNumber: string = "";
    /**
    * Order Source. Assigned at order creation. Not editable. : 0 = None, 1 = BrewMan, 2 = WooCommerce, 3 = Shopify, 4 = Sellar, 5 = Zettle, 6 = Other, 7 = EposNow, 8 = Beerflex, 9 = Square, 10 = Squarespace, 11 = Ecwid, 12 = EeBria, 13 = Commerce7, 14 = TradePortal, 15 = Amazon, 16 = BigCommerce, 17 = GoTab
    */
    public orderSource: EnumOrderSource = EnumOrderSource.none;
    /**
    * Outlet which the order is for. Shown on docs. Used to determine which contact in accounts the order is sent to (if not invoiced to group). Outlet is required at order create and then Not editable.
    */
    public outletId: Guid = Guid.createEmpty();
    /**
    * Outlet Name. Used in distribution planning / delivery app. Captured from outlet at order creation. Not editable.
    * Not Nullable
    * Max Length : 1000
    */
    public outletName: string = "";
    /**
    * Outlet Rating of Outlet at Time of order. No current Function - Stored for potential future reporting. Captured from outlet at order creation. Not editable.
    */
    public outletRatingId: Guid = Guid.createEmpty();
    private _outletTypeId: Guid | null = null;
    /**
    * Outlet Type of Outlet at Time of order. Stored for potential future reporting. Also used for GlCode resolution when lines added. Captured from outlet at order creation. Not editable.
    */
    public get outletTypeId(): Guid | null { return this._outletTypeId }
    public set outletTypeId(value: Guid | null) {
      this._outletTypeId = value === undefined ? null : value;
    }
    private _salesAreaId: Guid | null = null;
    /**
    * The Sales Area which the outlet was in at the time of the order. No current Function - Stored for potential future reporting. Captured from outlet at order creation. Not editable.
    */
    public get salesAreaId(): Guid | null { return this._salesAreaId }
    public set salesAreaId(value: Guid | null) {
      this._salesAreaId = value === undefined ? null : value;
    }
    private _salesCodeId: Guid | null = null;
    /**
    * The Sales Code which the outlet was in at the time of the order. No current Function - Stored for potential future reporting. Captured from outlet at order creation. Not editable.
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    /**
    * Processing Status Of the order. Not directly editable, but updated as explicit state change operations (complete/cancel/archive) are invoked. Orders are created with Status Open. : 0 = None, 1 = Open, 2 = Complete, 3 = Historic, 4 = Cancelled, 5 = Posting, 6 = Draft
    */
    public status: EnumOrderStatus = EnumOrderStatus.none;
    /**
    * Total Cost Price of Order. (Inc Duty as applicable, Ex VAT). Essentially a cached value of the sum of NetCost of lines. Updated when lines changed. Not editable.
    */
    public totalCostNet: number = 0;
    /**
    * Total Net Price of Order. Essentially a cached value of the sum of NetPrice of lines. Updated when lines changed. Not editable.
    */
    public totalNetPrice: number = 0;
    /**
    * Total VAT of Order. Essentially a cached value of the sum of Vat of lines. Updated when lines changed. Not editable.
    */
    public totalVat: number = 0;
    /**
    * User who initially created the order. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Description of the current error which is preventing this credit from being posted to accounts. Ie the error (if any) which occurred  when this was last attempted to post. The presence of text in this field implies there is a known current error so posting will not be reattempted. To reattempt posting this field must be cleared.
    * Not Nullable
    * Max Length : 100000
    */
    public currentPostingError: string = "";
    /**
    * Set to true when stock is adjusted for this order (occurs once posting to accounts succeeds). When true it implies that the items on the order should not be modified.
    */
    public hasStockBeenAdjusted: boolean = false;
    /**
    * The approximate amount of duty which has/will be paid in fulfilling this order. This represents the amount of duty included in the total net price, regardless of whether that duty would be chargeable directly due to this specific sale. For example, if the sale is from non-bonded then the duty has already been paid as part of the stock transfer to non-bonded so this sale will not cause a direct duty liability but that duty element will be included in the duty cost of sale. It is approximate because actual duty return calculations will be rounded across multiple orders so cannot be exactly attributed to each individual order. 
    */
    public totalApproximateDutyCostOfSale: number = 0;
    /**
    * Total Regional Tax of Order.  Updated when lines changed. Not editable.
    */
    public totalRegionalTax: number = 0;
    /**
    * The exchange rate used to convert monetary values from the order's currency to the tenant's base currency. Not editable.
    */
    public conversionFactor: number = 0;
    /**
    * The ISO 4217 Currency Code of this order.
    */
    public currencyCode: EnumCurrency = EnumCurrency.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.lastUpdatedAt = Instant.parse(jsonSerializedObject.lastUpdatedAt);
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.cancellationReason = jsonSerializedObject.cancellationReason;
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.creditTermsDescription = jsonSerializedObject.creditTermsDescription;
          this.deliveryLatitude = jsonSerializedObject.deliveryLatitude == null ? null : jsonSerializedObject.deliveryLatitude;
          this.deliveryLongitude = jsonSerializedObject.deliveryLongitude == null ? null : jsonSerializedObject.deliveryLongitude;
          this.externalReference = jsonSerializedObject.externalReference;
          this.groupId = jsonSerializedObject.groupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.invoiceNumber = jsonSerializedObject.invoiceNumber;
          this.isRetailOrder = jsonSerializedObject.isRetailOrder;
          this.orderNumber = jsonSerializedObject.orderNumber;
          this.orderSource = EnumOrderSource.getByValue(jsonSerializedObject.orderSource, this.orderSource);
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.outletName = jsonSerializedObject.outletName;
          this.outletRatingId = Guid.parseOrEmpty(jsonSerializedObject.outletRatingId);
          this.outletTypeId = jsonSerializedObject.outletTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.salesAreaId = jsonSerializedObject.salesAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesAreaId);
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.status = EnumOrderStatus.getByValue(jsonSerializedObject.status, this.status);
          this.totalCostNet = jsonSerializedObject.totalCostNet;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.totalVat = jsonSerializedObject.totalVat;
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.currentPostingError = jsonSerializedObject.currentPostingError;
          this.hasStockBeenAdjusted = jsonSerializedObject.hasStockBeenAdjusted;
          this.totalApproximateDutyCostOfSale = jsonSerializedObject.totalApproximateDutyCostOfSale;
          this.totalRegionalTax = jsonSerializedObject.totalRegionalTax;
          this.conversionFactor = jsonSerializedObject.conversionFactor;
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.lastUpdatedAt = this.lastUpdatedAt.toString();
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.cancellationReason = this.cancellationReason;
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.creditTermsDescription = this.creditTermsDescription;
        toRet.deliveryLatitude = this.deliveryLatitude == null ? null : this.deliveryLatitude;
        toRet.deliveryLongitude = this.deliveryLongitude == null ? null : this.deliveryLongitude;
        toRet.externalReference = this.externalReference;
        toRet.groupId = this.groupId == null ? null : this.groupId.toString();
        toRet.invoiceNumber = this.invoiceNumber;
        toRet.isRetailOrder = this.isRetailOrder;
        toRet.orderNumber = this.orderNumber;
        toRet.orderSource = this.orderSource.value;
        toRet.outletId = this.outletId.toString();
        toRet.outletName = this.outletName;
        toRet.outletRatingId = this.outletRatingId.toString();
        toRet.outletTypeId = this.outletTypeId == null ? null : this.outletTypeId.toString();
        toRet.salesAreaId = this.salesAreaId == null ? null : this.salesAreaId.toString();
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.status = this.status.value;
        toRet.totalCostNet = this.totalCostNet;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.totalVat = this.totalVat;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.currentPostingError = this.currentPostingError;
        toRet.hasStockBeenAdjusted = this.hasStockBeenAdjusted;
        toRet.totalApproximateDutyCostOfSale = this.totalApproximateDutyCostOfSale;
        toRet.totalRegionalTax = this.totalRegionalTax;
        toRet.conversionFactor = this.conversionFactor;
        toRet.currencyCode = this.currencyCode.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "OrderHeaderReadOnly.Id cannot be empty"},
            {field: "cancellationReason", rule: (v: any) => v == null || v.length <= 100000 || "CancellationReason has too many characters (100000)"},
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeaderReadOnly.CancelledByUserId cannot be empty"},
            {field: "creditTermsDescription", rule: (v: any) => v == null || v.length <= 1000 || "CreditTermsDescription has too many characters (1000)"},
            {field: "externalReference", rule: (v: any) => v == null || v.length <= 1000 || "ExternalReference has too many characters (1000)"},
            {field: "groupId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeaderReadOnly.GroupId cannot be empty"},
            {field: "invoiceNumber", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceNumber has too many characters (1000)"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OrderHeaderReadOnly.OutletId cannot be empty"},
            {field: "outletName", rule: (v: any) => v == null || v.length <= 1000 || "OutletName has too many characters (1000)"},
            {field: "outletRatingId", rule: (v: any) => !v.isEmpty() || "OrderHeaderReadOnly.OutletRatingId cannot be empty"},
            {field: "outletTypeId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeaderReadOnly.OutletTypeId cannot be empty"},
            {field: "salesAreaId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeaderReadOnly.SalesAreaId cannot be empty"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeaderReadOnly.SalesCodeId cannot be empty"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "OrderHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "currentPostingError", rule: (v: any) => v == null || v.length <= 100000 || "CurrentPostingError has too many characters (100000)"},
    ];
}


export class OrderHeader extends ModelObjectWithDirtyCheck<OrderHeader> {

    private _collectionTimeId: Guid | null = null;
    /**
    * Collection Time. Just for user information.
    */
    public get collectionTimeId(): Guid | null { return this._collectionTimeId }
    public set collectionTimeId(value: Guid | null) {
      this._collectionTimeId = value === undefined ? null : value;
    }
    private _collectionTime: LocalTime | null = null;
    /**
    * The time the order will be collected. Just for user information.
    */
    public get collectionTime(): LocalTime | null { return this._collectionTime }
    public set collectionTime(value: LocalTime | null) {
      this._collectionTime = value === undefined ? null : value;
    }
    private _courierId: Guid | null = null;
    /**
    * Courier. Defaults to Courier chosen for relevant Outlet. Required if distribution type is Courier.
    */
    public get courierId(): Guid | null { return this._courierId }
    public set courierId(value: Guid | null) {
      this._courierId = value === undefined ? null : value;
    }
    /**
    * Customer Reference. Displays on documents, sent to accounts packages.
    * Not Nullable
    * Max Length : 1000
    */
    public customerReference: string = "";
    /**
    * Delivery Recipient Name. Just for user information. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryRecipient: string = "";
    /**
    * The email address to use for delivery issues and updates. Just for user information. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryEmailAddress: string = "";
    /**
    * The phone number to use for delivery issues and updates. Just for user information. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryTelephoneNumber: string = "";
    /**
    * Delivery Business Name. Displays on documents. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddressBusiness: string = "";
    /**
    * Delivery Address Line 1. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress1: string = "";
    /**
    * Delivery Address Line 2. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress2: string = "";
    /**
    * Delivery Address Line 3. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress3: string = "";
    /**
    * Delivery Address Line 4. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress4: string = "";
    /**
    * Delivery Address Line 5 - County. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress5: string = "";
    /**
    * Delivery Address Line 6 - Country. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryAddress6: string = "";
    private _deliveryAreaId: Guid | null = null;
    /**
    * Delivery Area. Displays in Delivery App. Copies from Outlet by default.
    */
    public get deliveryAreaId(): Guid | null { return this._deliveryAreaId }
    public set deliveryAreaId(value: Guid | null) {
      this._deliveryAreaId = value === undefined ? null : value;
    }
    /**
    * Delivery Post Code. Displays in delivery app and documents. Sent to accounts. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public deliveryPostcode: string = "";
    /**
    * User notes relating to dispatch of this order. Displays on documents. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 100000
    */
    public externalDispatchNotes: string = "";
    /**
    * Date the order will be / was Delivered. Sent to accounts as the Invoice Date for the order.
    */
    public deliveryDate: LocalDate = LocalDate.ofEpochDay(0);
    private _deliveryTimeEnd: LocalTime | null = null;
    /**
    * End of delivery window. Used for distribution planning and shown in delivery app. Copies from Outlet by default.
    */
    public get deliveryTimeEnd(): LocalTime | null { return this._deliveryTimeEnd }
    public set deliveryTimeEnd(value: LocalTime | null) {
      this._deliveryTimeEnd = value === undefined ? null : value;
    }
    private _deliveryTimeStart: LocalTime | null = null;
    /**
    * Start of delivery window. Used for distribution planning and shown in delivery app. Copies from Outlet by default.
    */
    public get deliveryTimeStart(): LocalTime | null { return this._deliveryTimeStart }
    public set deliveryTimeStart(value: LocalTime | null) {
      this._deliveryTimeStart = value === undefined ? null : value;
    }
    /**
    * Date the order was/will be despatched for delivery. This is also the date at which any duty liability for the order occurs.
    */
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Distribution Type. Drives the manner of distribution, particularly type Delivery which is included in the distribution planning and delivery app. Copied from Outlet by default. : 0 = None, 1 = Delivery, 2 = Courier, 3 = Collection
    */
    public distributionType: EnumDistributionType = EnumDistributionType.none;
    private _dutyReturnDateOverride: LocalDate | null = null;
    /**
    * Special Effective Duty Date for this order. Normally will be null, but can be set if the order is put on BrewMan after the natural duty return has been submitted.
    */
    public get dutyReturnDateOverride(): LocalDate | null { return this._dutyReturnDateOverride }
    public set dutyReturnDateOverride(value: LocalDate | null) {
      this._dutyReturnDateOverride = value === undefined ? null : value;
    }
    /**
    * Whether an invoice has been sent for this order. Just for user information. 
    */
    public invoiceSent: boolean = false;
    /**
    * The email address invoices will be sent to. Shown on docs. Used when invoices auto-emailed. Copied from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceEmailAddress: string = "";
    /**
    * Invoice Recipient Name. Just for user information. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceRecipient: string = "";
    /**
    * Invoice Business Name. Shown on docs. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddressBusiness: string = "";
    /**
    * Invoice Address Line 1. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress1: string = "";
    /**
    * Invoice Address Line 2. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress2: string = "";
    /**
    * Invoice Address Line 3. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress3: string = "";
    /**
    * Invoice Address Line 4. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress4: string = "";
    /**
    * Invoice Address Line 5 - County. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress5: string = "";
    /**
    * Invoice Address Line 6 - Country. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoiceAddress6: string = "";
    /**
    * Invoice Post Code. Shown on docs. Sent to accounts. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public invoicePostcode: string = "";
    /**
    * Our Reference. Just for user information.
    * Not Nullable
    * Max Length : 1000
    */
    public ourReference: string = "";
    /**
    * Price List used for the order. Affects price calculations and discounts. Controls which products are allowed.
    */
    public priceListId: Guid = Guid.createEmpty();
    /**
    * Retail Payment Method. Used for grouping retail orders when creating summaries to post to accounts. : 0 = None, 1 = DebitCreditCard, 2 = Other, 3 = Cash
    */
    public retailPaymentMethod: EnumRetailPaymentMethod = EnumRetailPaymentMethod.none;
    /**
    * Sales Order Notes. Shown on docs. Sent to accounts.
    * Not Nullable
    * Max Length : 100000
    */
    public salesOrderNotes: string = "";
    /**
    * Order Type. Determines the duty implication of the order. Affects weight allocated to lines (export/normal). Can be changed, but all Order Lines should be removed if StandardOrderType is changed. : 0 = FullPriceDutyPaid, 1 = PriceExcludingDutyDutySuspended, 2 = FullPriceDutyPaidElsewhere, 3 = ExportDutySuspended, 4 = ConsumeOnPremisesNoDuty
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    /**
    * Expected duration required for a delivery stop, in minutes. Used in distribution planning. Copied from Outlet by default.
    */
    public stopMinutes: number = 0;
    /**
    * Tracking Reference. Just for user information.
    * Not Nullable
    * Max Length : 1000
    */
    public trackingReference: string = "";
    /**
    * Vat Code Id applicable to the order. If this is zero-rated then it will be used for lines added to the order instead of the individual product's Vat Code Id. Copied from Outlet by default. 
    */
    public vatCodeId: Guid = Guid.createEmpty();
    public readOnly: OrderHeaderReadOnly = new OrderHeaderReadOnly();
    /**
    * Private notes relating to dispatch of this order. Not displayed to customer. Copies from Outlet by default.
    * Not Nullable
    * Max Length : 100000
    */
    public internalDispatchNotes: string = "";
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    /**
    * Reference name or number from the Accounts Link posting.
    * Not Nullable
    * Max Length : 1000
    */
    public accountsLinkReferenceName: string = "";
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;
    private _validUntilDate: LocalDate | null = null;
    /**
    * Date until which a draft order is valid.
    */
    public get validUntilDate(): LocalDate | null { return this._validUntilDate }
    public set validUntilDate(value: LocalDate | null) {
      this._validUntilDate = value === undefined ? null : value;
    }
    /**
    * Nature of transaction code.
    */
    public natureOfTransactionCode: EnumNatureOfTransactionCode = EnumNatureOfTransactionCode.none;
    /**
    * Whether an invoice has been printed or downloaded for this order. Just for user information. 
    */
    public invoicePrinted: boolean = false;
    private _discountPercentage: number | null = null;
    /**
    * Discount in percentage applied to the whole order and on top of line discounts.
    */
    public get discountPercentage(): number | null { return this._discountPercentage }
    public set discountPercentage(value: number | null) {
      this._discountPercentage = value === undefined ? null : value;
    }
    /**
    * Whether a dispatch note has been printed or downloaded for this order. Just for user information. 
    */
    public dispatchNotePrinted: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.collectionTimeId = jsonSerializedObject.collectionTimeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.collectionTimeId);
          this.collectionTime = jsonSerializedObject.collectionTime == null ? null : LocalTime.parse(jsonSerializedObject.collectionTime);
          this.courierId = jsonSerializedObject.courierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.courierId);
          this.customerReference = jsonSerializedObject.customerReference;
          this.deliveryRecipient = jsonSerializedObject.deliveryRecipient;
          this.deliveryEmailAddress = jsonSerializedObject.deliveryEmailAddress;
          this.deliveryTelephoneNumber = jsonSerializedObject.deliveryTelephoneNumber;
          this.deliveryAddressBusiness = jsonSerializedObject.deliveryAddressBusiness;
          this.deliveryAddress1 = jsonSerializedObject.deliveryAddress1;
          this.deliveryAddress2 = jsonSerializedObject.deliveryAddress2;
          this.deliveryAddress3 = jsonSerializedObject.deliveryAddress3;
          this.deliveryAddress4 = jsonSerializedObject.deliveryAddress4;
          this.deliveryAddress5 = jsonSerializedObject.deliveryAddress5;
          this.deliveryAddress6 = jsonSerializedObject.deliveryAddress6;
          this.deliveryAreaId = jsonSerializedObject.deliveryAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.deliveryAreaId);
          this.deliveryPostcode = jsonSerializedObject.deliveryPostcode;
          this.externalDispatchNotes = jsonSerializedObject.externalDispatchNotes;
          this.deliveryDate = LocalDate.parse(jsonSerializedObject.deliveryDate);
          this.deliveryTimeEnd = jsonSerializedObject.deliveryTimeEnd == null ? null : LocalTime.parse(jsonSerializedObject.deliveryTimeEnd);
          this.deliveryTimeStart = jsonSerializedObject.deliveryTimeStart == null ? null : LocalTime.parse(jsonSerializedObject.deliveryTimeStart);
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.distributionType = EnumDistributionType.getByValue(jsonSerializedObject.distributionType, this.distributionType);
          this.dutyReturnDateOverride = jsonSerializedObject.dutyReturnDateOverride == null ? null : LocalDate.parse(jsonSerializedObject.dutyReturnDateOverride);
          this.invoiceSent = jsonSerializedObject.invoiceSent;
          this.invoiceEmailAddress = jsonSerializedObject.invoiceEmailAddress;
          this.invoiceRecipient = jsonSerializedObject.invoiceRecipient;
          this.invoiceAddressBusiness = jsonSerializedObject.invoiceAddressBusiness;
          this.invoiceAddress1 = jsonSerializedObject.invoiceAddress1;
          this.invoiceAddress2 = jsonSerializedObject.invoiceAddress2;
          this.invoiceAddress3 = jsonSerializedObject.invoiceAddress3;
          this.invoiceAddress4 = jsonSerializedObject.invoiceAddress4;
          this.invoiceAddress5 = jsonSerializedObject.invoiceAddress5;
          this.invoiceAddress6 = jsonSerializedObject.invoiceAddress6;
          this.invoicePostcode = jsonSerializedObject.invoicePostcode;
          this.ourReference = jsonSerializedObject.ourReference;
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.retailPaymentMethod = EnumRetailPaymentMethod.getByValue(jsonSerializedObject.retailPaymentMethod, this.retailPaymentMethod);
          this.salesOrderNotes = jsonSerializedObject.salesOrderNotes;
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.stopMinutes = jsonSerializedObject.stopMinutes;
          this.trackingReference = jsonSerializedObject.trackingReference;
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.readOnly = new OrderHeaderReadOnly(jsonSerializedObject.readOnly);
          this.internalDispatchNotes = jsonSerializedObject.internalDispatchNotes;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.accountsLinkReferenceName = jsonSerializedObject.accountsLinkReferenceName;
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
          this.validUntilDate = jsonSerializedObject.validUntilDate == null ? null : LocalDate.parse(jsonSerializedObject.validUntilDate);
          this.natureOfTransactionCode = EnumNatureOfTransactionCode.getByValue(jsonSerializedObject.natureOfTransactionCode, this.natureOfTransactionCode);
          this.invoicePrinted = jsonSerializedObject.invoicePrinted;
          this.discountPercentage = jsonSerializedObject.discountPercentage == null ? null : jsonSerializedObject.discountPercentage;
          this.dispatchNotePrinted = jsonSerializedObject.dispatchNotePrinted;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.collectionTimeId = this.collectionTimeId == null ? null : this.collectionTimeId.toString();
        toRet.collectionTime = this.collectionTime == null ? null : this.collectionTime.toString();
        toRet.courierId = this.courierId == null ? null : this.courierId.toString();
        toRet.customerReference = this.customerReference;
        toRet.deliveryRecipient = this.deliveryRecipient;
        toRet.deliveryEmailAddress = this.deliveryEmailAddress;
        toRet.deliveryTelephoneNumber = this.deliveryTelephoneNumber;
        toRet.deliveryAddressBusiness = this.deliveryAddressBusiness;
        toRet.deliveryAddress1 = this.deliveryAddress1;
        toRet.deliveryAddress2 = this.deliveryAddress2;
        toRet.deliveryAddress3 = this.deliveryAddress3;
        toRet.deliveryAddress4 = this.deliveryAddress4;
        toRet.deliveryAddress5 = this.deliveryAddress5;
        toRet.deliveryAddress6 = this.deliveryAddress6;
        toRet.deliveryAreaId = this.deliveryAreaId == null ? null : this.deliveryAreaId.toString();
        toRet.deliveryPostcode = this.deliveryPostcode;
        toRet.externalDispatchNotes = this.externalDispatchNotes;
        toRet.deliveryDate = this.deliveryDate.toString();
        toRet.deliveryTimeEnd = this.deliveryTimeEnd == null ? null : this.deliveryTimeEnd.toString();
        toRet.deliveryTimeStart = this.deliveryTimeStart == null ? null : this.deliveryTimeStart.toString();
        toRet.despatchDate = this.despatchDate.toString();
        toRet.distributionType = this.distributionType.value;
        toRet.dutyReturnDateOverride = this.dutyReturnDateOverride == null ? null : this.dutyReturnDateOverride.toString();
        toRet.invoiceSent = this.invoiceSent;
        toRet.invoiceEmailAddress = this.invoiceEmailAddress;
        toRet.invoiceRecipient = this.invoiceRecipient;
        toRet.invoiceAddressBusiness = this.invoiceAddressBusiness;
        toRet.invoiceAddress1 = this.invoiceAddress1;
        toRet.invoiceAddress2 = this.invoiceAddress2;
        toRet.invoiceAddress3 = this.invoiceAddress3;
        toRet.invoiceAddress4 = this.invoiceAddress4;
        toRet.invoiceAddress5 = this.invoiceAddress5;
        toRet.invoiceAddress6 = this.invoiceAddress6;
        toRet.invoicePostcode = this.invoicePostcode;
        toRet.ourReference = this.ourReference;
        toRet.priceListId = this.priceListId.toString();
        toRet.retailPaymentMethod = this.retailPaymentMethod.value;
        toRet.salesOrderNotes = this.salesOrderNotes;
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.stopMinutes = this.stopMinutes;
        toRet.trackingReference = this.trackingReference;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.internalDispatchNotes = this.internalDispatchNotes;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.accountsLinkReferenceName = this.accountsLinkReferenceName;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        toRet.validUntilDate = this.validUntilDate == null ? null : this.validUntilDate.toString();
        toRet.natureOfTransactionCode = this.natureOfTransactionCode.value;
        toRet.invoicePrinted = this.invoicePrinted;
        toRet.discountPercentage = this.discountPercentage == null ? null : this.discountPercentage;
        toRet.dispatchNotePrinted = this.dispatchNotePrinted;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "collectionTimeId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeader.CollectionTimeId cannot be empty"},
            {field: "courierId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeader.CourierId cannot be empty"},
            {field: "customerReference", rule: (v: any) => v == null || v.length <= 1000 || "CustomerReference has too many characters (1000)"},
            {field: "deliveryRecipient", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryRecipient has too many characters (1000)"},
            {field: "deliveryEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryEmailAddress has too many characters (1000)"},
            {field: "deliveryTelephoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryTelephoneNumber has too many characters (1000)"},
            {field: "deliveryAddressBusiness", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddressBusiness has too many characters (1000)"},
            {field: "deliveryAddress1", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress1 has too many characters (1000)"},
            {field: "deliveryAddress2", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress2 has too many characters (1000)"},
            {field: "deliveryAddress3", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress3 has too many characters (1000)"},
            {field: "deliveryAddress4", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress4 has too many characters (1000)"},
            {field: "deliveryAddress5", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress5 has too many characters (1000)"},
            {field: "deliveryAddress6", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryAddress6 has too many characters (1000)"},
            {field: "deliveryAreaId", rule: (v: any) => v == null || !v.isEmpty() || "OrderHeader.DeliveryAreaId cannot be empty"},
            {field: "deliveryPostcode", rule: (v: any) => v == null || v.length <= 1000 || "DeliveryPostcode has too many characters (1000)"},
            {field: "externalDispatchNotes", rule: (v: any) => v == null || v.length <= 100000 || "ExternalDispatchNotes has too many characters (100000)"},
            {field: "invoiceEmailAddress", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceEmailAddress has too many characters (1000)"},
            {field: "invoiceRecipient", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceRecipient has too many characters (1000)"},
            {field: "invoiceAddressBusiness", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddressBusiness has too many characters (1000)"},
            {field: "invoiceAddress1", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress1 has too many characters (1000)"},
            {field: "invoiceAddress2", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress2 has too many characters (1000)"},
            {field: "invoiceAddress3", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress3 has too many characters (1000)"},
            {field: "invoiceAddress4", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress4 has too many characters (1000)"},
            {field: "invoiceAddress5", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress5 has too many characters (1000)"},
            {field: "invoiceAddress6", rule: (v: any) => v == null || v.length <= 1000 || "InvoiceAddress6 has too many characters (1000)"},
            {field: "invoicePostcode", rule: (v: any) => v == null || v.length <= 1000 || "InvoicePostcode has too many characters (1000)"},
            {field: "ourReference", rule: (v: any) => v == null || v.length <= 1000 || "OurReference has too many characters (1000)"},
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "OrderHeader.PriceListId cannot be empty"},
            {field: "salesOrderNotes", rule: (v: any) => v == null || v.length <= 100000 || "SalesOrderNotes has too many characters (100000)"},
            {field: "stopMinutes", rule: (v: any) => v == null || v >= -2147483648 || "StopMinutes is not greater than minimum allowed value: -2147483648"},
            {field: "stopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "StopMinutes is above maximum allowed value: 2147483647"},
            {field: "trackingReference", rule: (v: any) => v == null || v.length <= 1000 || "TrackingReference has too many characters (1000)"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "OrderHeader.VatCodeId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "internalDispatchNotes", rule: (v: any) => v == null || v.length <= 100000 || "InternalDispatchNotes has too many characters (100000)"},
            {field: "accountsLinkReferenceName", rule: (v: any) => v == null || v.length <= 1000 || "AccountsLinkReferenceName has too many characters (1000)"},
    ];
}


export class OrderLineReadOnly extends ModelObject<OrderLineReadOnly> {

    public id: Guid = Guid.createEmpty();
    /**
    * Product Abv. Used largely in duty calculations. Captured from product when adding line to order. Not editable.
    * Not Negative
    */
    public abv: number = 0;
    /**
    * Alcohol Type. Used largely in duty calculations. Captured from product when adding line to order. Not editable.
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * Discount Basis. Determines the meaning of UnitDiscount (or UnitDiscountOverride). Captured from the discount applicable when the line was added to the order. Not editable. : 0 = None, 1 = Percent, 2 = CurrencyPerUnit, 3 = CurrencyPerBarrel, 4 = CurrencyPerLitre
    */
    public discountBasis: EnumDiscountBasis = EnumDiscountBasis.none;
    /**
    * Line Cost Price (Inc Duty, Exc Vat). Used for profit calculations. Calculated when line is added to order, or order lines are edited. Not editable.
    */
    public lineCostNetPrice: number = 0;
    /**
    * Net (no VAT) Price charged for the line. Not directly editable, calculated from other line properties (List Price, Discount)
    */
    public lineNetPrice: number = 0;
    /**
    * Total VAT for the line. Not directly editable, calculated from other line properties (List Price, Discount)
    */
    public lineVat: number = 0;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer of the product. Can affect duty (particularly UK Beer Duty). Required if AlcoholType is not None. Copied from Product when line added to order. Not editable.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * Order Header Id
    */
    public orderHeaderId: Guid = Guid.createEmpty();
    private _productBrandId: Guid | null = null;
    /**
    * Product Brand at time of Order. No current functionality. Stored for potential future reporting. Captured from product when line is added to order. Not editable.
    */
    public get productBrandId(): Guid | null { return this._productBrandId }
    public set productBrandId(value: Guid | null) {
      this._productBrandId = value === undefined ? null : value;
    }
    /**
    * Product Code at time of adding line to order. Captured so reprinted invoices etc show original code. Captured from product when line is added to order. Not editable.
    * Not Nullable
    * Max Length : 1000
    */
    public productCode: string = "";
    /**
    * Stock Item Id. Given when line is added to order. Not editable.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Product Cost Price. Calculated when line is added to order, or order lines are edited. Not editable.
    */
    public unitCostNetPrice: number = 0;
    /**
    * Product Dutiable Litres.
    * Not Negative
    */
    public unitDutiableLitres: number = 0;
    /**
    * Auto-Calculated Discount amount per unit. DiscountBasis identifies how this is interpreted (eg % discount or CurrencyPerUnit Ignored if UnitDiscountOverride is specified. Calculated when line is added to order, or order lines are edited. Not editable.
    * Not Negative
    */
    public unitDiscount: number = 0;
    /**
    * Product Unit Litres. Affects liquid-volume based discounts. Captured from the package type of the product when line is added to order. Not editable.
    * Not Negative
    */
    public unitLitres: number = 0;
    /**
    * Unit Net Price. Evaluated from other pricing information. Not editable.
    */
    public unitNetPrice: number = 0;
    private _pricingCategoryId: Guid | null = null;
    /**
    * Pricing Category. Used for evaluating and reevaluating price/discounts. Captured from product when line is added to order. Not editable
    */
    public get pricingCategoryId(): Guid | null { return this._pricingCategoryId }
    public set pricingCategoryId(value: Guid | null) {
      this._pricingCategoryId = value === undefined ? null : value;
    }
    /**
    * The approximate amount of duty which has/will be paid in fulfilling this order line. This represents the amount of duty included in the line net price, regardless of whether that duty would be chargeable directly due to this specific sale. For example, if the sale is from non-bonded then the duty has already been paid as part of the stock transfer to non-bonded so this sale will not cause a direct duty liability but that duty element will be included in the duty cost of sale. It is approximate because actual duty return calculations will be rounded across multiple orders so cannot be exactly attributed to each individual order. 
    */
    public approximateDutyCostOfSale: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.abv = jsonSerializedObject.abv;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.discountBasis = EnumDiscountBasis.getByValue(jsonSerializedObject.discountBasis, this.discountBasis);
          this.lineCostNetPrice = jsonSerializedObject.lineCostNetPrice;
          this.lineNetPrice = jsonSerializedObject.lineNetPrice;
          this.lineVat = jsonSerializedObject.lineVat;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.orderHeaderId = Guid.parseOrEmpty(jsonSerializedObject.orderHeaderId);
          this.productBrandId = jsonSerializedObject.productBrandId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBrandId);
          this.productCode = jsonSerializedObject.productCode;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitCostNetPrice = jsonSerializedObject.unitCostNetPrice;
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.unitDiscount = jsonSerializedObject.unitDiscount;
          this.unitLitres = jsonSerializedObject.unitLitres;
          this.unitNetPrice = jsonSerializedObject.unitNetPrice;
          this.pricingCategoryId = jsonSerializedObject.pricingCategoryId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.pricingCategoryId);
          this.approximateDutyCostOfSale = jsonSerializedObject.approximateDutyCostOfSale;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.abv = this.abv;
        toRet.alcoholType = this.alcoholType.value;
        toRet.discountBasis = this.discountBasis.value;
        toRet.lineCostNetPrice = this.lineCostNetPrice;
        toRet.lineNetPrice = this.lineNetPrice;
        toRet.lineVat = this.lineVat;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.orderHeaderId = this.orderHeaderId.toString();
        toRet.productBrandId = this.productBrandId == null ? null : this.productBrandId.toString();
        toRet.productCode = this.productCode;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitCostNetPrice = this.unitCostNetPrice;
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.unitDiscount = this.unitDiscount;
        toRet.unitLitres = this.unitLitres;
        toRet.unitNetPrice = this.unitNetPrice;
        toRet.pricingCategoryId = this.pricingCategoryId == null ? null : this.pricingCategoryId.toString();
        toRet.approximateDutyCostOfSale = this.approximateDutyCostOfSale;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "OrderLineReadOnly.Id cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLineReadOnly.ManufacturerId cannot be empty"},
            {field: "orderHeaderId", rule: (v: any) => !v.isEmpty() || "OrderLineReadOnly.OrderHeaderId cannot be empty"},
            {field: "productBrandId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLineReadOnly.ProductBrandId cannot be empty"},
            {field: "productCode", rule: (v: any) => v == null || v.length <= 1000 || "ProductCode has too many characters (1000)"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "OrderLineReadOnly.StockItemId cannot be empty"},
            {field: "pricingCategoryId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLineReadOnly.PricingCategoryId cannot be empty"},
    ];
}


export class OrderLineCalculated extends ModelObject<OrderLineCalculated> {

    private _availableQuantity: number | null = null;
    /**
    * Maximum Quantity which line can be changed to without causing over-commitment. Null if stock not checked or not applicable.
    */
    public get availableQuantity(): number | null { return this._availableQuantity }
    public set availableQuantity(value: number | null) {
      this._availableQuantity = value === undefined ? null : value;
    }
    /**
    * True if the cumulative discount which could apply has been evaluated. False if not checked or not applicable.
    */
    public availableDiscountEvaluated: boolean = false;
    private _availableCumulativeDiscountId: Guid | null = null;
    public get availableCumulativeDiscountId(): Guid | null { return this._availableCumulativeDiscountId }
    public set availableCumulativeDiscountId(value: Guid | null) {
      this._availableCumulativeDiscountId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.availableQuantity = jsonSerializedObject.availableQuantity == null ? null : jsonSerializedObject.availableQuantity;
          this.availableDiscountEvaluated = jsonSerializedObject.availableDiscountEvaluated;
          this.availableCumulativeDiscountId = jsonSerializedObject.availableCumulativeDiscountId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.availableCumulativeDiscountId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.availableQuantity = this.availableQuantity == null ? null : this.availableQuantity;
        toRet.availableDiscountEvaluated = this.availableDiscountEvaluated;
        toRet.availableCumulativeDiscountId = this.availableCumulativeDiscountId == null ? null : this.availableCumulativeDiscountId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "availableCumulativeDiscountId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLineCalculated.AvailableCumulativeDiscountId cannot be empty"},
    ];
}


export class OrderLine extends ModelObject<OrderLine> {

    /**
    * Product Name. Stored so invoice can be recreated. Can be edited (useful for eg Miscellaneous / Guest products). Defaults to name of product when line is added to order.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public productName: string = "";
    /**
    * Quantity Ordered
    * Not Negative
    */
    public quantity: number = 0;
    /**
    * Stock Location Id from which this line is despatched.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _unitDiscountOverride: number | null = null;
    /**
    * Unit Discount Override. If not null then this discount level is applied (with meaning given by DiscountBasis) instead of the auto-calculated UnitDiscount.
    */
    public get unitDiscountOverride(): number | null { return this._unitDiscountOverride }
    public set unitDiscountOverride(value: number | null) {
      this._unitDiscountOverride = value === undefined ? null : value;
    }
    /**
    * Discount Override Basis. Determines the meaning of UnitDiscountOverride. : 0 = None, 1 = Percent, 2 = CurrencyPerUnit, 3 = CurrencyPerBarrel, 4 = CurrencyPerLitre
    */
    public discountOverrideBasis: EnumDiscountBasis = EnumDiscountBasis.none;
    /**
    * Unit List Net Price. This is the base list price before any applicable discount. Captured from Price List when line is added to order, but can be edited.
    */
    public unitListNetPrice: number = 0;
    private _unitListGrossPriceForRounding: number | null = null;
    /**
    * Populated with gross product list price from the price list. Used to ensure tax calculations round to the gross value correctly. Null if no rounding is necessary.
    */
    public get unitListGrossPriceForRounding(): number | null { return this._unitListGrossPriceForRounding }
    public set unitListGrossPriceForRounding(value: number | null) {
      this._unitListGrossPriceForRounding = value === undefined ? null : value;
    }
    /**
    * Unit of Measure Id. Captured from product when line is added to order. Not editable.
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public readOnly: OrderLineReadOnly = new OrderLineReadOnly();
    public calculated: OrderLineCalculated = new OrderLineCalculated();
    public version: number = 0;
    /**
    * Sequence for ordering lines.
    */
    public sequence: number = 0;
    private _bogofId: Guid | null = null;
    /**
    * The Bogof offer, if any, for which this is the free product.
    */
    public get bogofId(): Guid | null { return this._bogofId }
    public set bogofId(value: Guid | null) {
      this._bogofId = value === undefined ? null : value;
    }
    private _plato: number | null = null;
    /**
    * Plato Value
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Set true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment. This is a service offered by breweries in some countries, such as Australia, and can affect duty.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * Set true if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs. Some duty systems have different duty rates for beer packaged in these manners for selling to pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant. For example, a case of 12 x 500ml bottles would have a final unit litres of 0.5. Some duty systems have different rates depending upon this value.
    * Greater Than : 0
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    /**
    * Vat Percentage at time of order.
    * Not Negative
    */
    public vatPercentage: number = 0;
    /**
    * Vat Code Applicable to the line.
    */
    public vatCodeId: Guid = Guid.createEmpty();
    /**
    * General Ledger Code of Line. Sent to accounts (currently Xero only). Captured from Product (and possibly override for order OutletTypeId) when line is added to order. Not editable.
    */
    public glCodeId: Guid = Guid.createEmpty();
    private _dateOfProductionDutyRate: LocalDate | null = null;
    /**
    * An optional date identifying the duty rate in force at the time the item was produced. Used for UK alcohol duty, where products qualifying for SPR are charged at the rate in force at date of production, not date of crossing duty boundary. To make management easier we only track the date to granularity of rate changes, So eg a value of 2023-08-01 would represent the period from 2023-08-01 to 2024-01-31 because rates did not change in this period.
    */
    public get dateOfProductionDutyRate(): LocalDate | null { return this._dateOfProductionDutyRate }
    public set dateOfProductionDutyRate(value: LocalDate | null) {
      this._dateOfProductionDutyRate = value === undefined ? null : value;
    }
    private _eprBomId: Guid | null = null;
    /**
    * EPR BOM of the order line.
    */
    public get eprBomId(): Guid | null { return this._eprBomId }
    public set eprBomId(value: Guid | null) {
      this._eprBomId = value === undefined ? null : value;
    }
    /**
    * Batch URN allocated to this order line.
    * Not Nullable
    * Max Length : 1000
    */
    public batchUrn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.productName = jsonSerializedObject.productName;
          this.quantity = jsonSerializedObject.quantity;
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.unitDiscountOverride = jsonSerializedObject.unitDiscountOverride == null ? null : jsonSerializedObject.unitDiscountOverride;
          this.discountOverrideBasis = EnumDiscountBasis.getByValue(jsonSerializedObject.discountOverrideBasis, this.discountOverrideBasis);
          this.unitListNetPrice = jsonSerializedObject.unitListNetPrice;
          this.unitListGrossPriceForRounding = jsonSerializedObject.unitListGrossPriceForRounding == null ? null : jsonSerializedObject.unitListGrossPriceForRounding;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.readOnly = new OrderLineReadOnly(jsonSerializedObject.readOnly);
          this.calculated = new OrderLineCalculated(jsonSerializedObject.calculated);
          this.version = jsonSerializedObject.version;
          this.sequence = jsonSerializedObject.sequence;
          this.bogofId = jsonSerializedObject.bogofId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.bogofId);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.vatPercentage = jsonSerializedObject.vatPercentage;
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.glCodeId = Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
          this.dateOfProductionDutyRate = jsonSerializedObject.dateOfProductionDutyRate == null ? null : LocalDate.parse(jsonSerializedObject.dateOfProductionDutyRate);
          this.eprBomId = jsonSerializedObject.eprBomId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.eprBomId);
          this.batchUrn = jsonSerializedObject.batchUrn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.productName = this.productName;
        toRet.quantity = this.quantity;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.unitDiscountOverride = this.unitDiscountOverride == null ? null : this.unitDiscountOverride;
        toRet.discountOverrideBasis = this.discountOverrideBasis.value;
        toRet.unitListNetPrice = this.unitListNetPrice;
        toRet.unitListGrossPriceForRounding = this.unitListGrossPriceForRounding == null ? null : this.unitListGrossPriceForRounding;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.version = this.version;
        toRet.sequence = this.sequence;
        toRet.bogofId = this.bogofId == null ? null : this.bogofId.toString();
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.vatPercentage = this.vatPercentage;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.glCodeId = this.glCodeId.toString();
        toRet.dateOfProductionDutyRate = this.dateOfProductionDutyRate == null ? null : this.dateOfProductionDutyRate.toString();
        toRet.eprBomId = this.eprBomId == null ? null : this.eprBomId.toString();
        toRet.batchUrn = this.batchUrn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "productName", rule: (v: any) => v == null || v.length !== 0 || "ProductName cannot be empty"},
            {field: "productName", rule: (v: any) => v == null || v.length <= 1000 || "ProductName has too many characters (1000)"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "OrderLine.StockLocationId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "OrderLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "bogofId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLine.BogofId cannot be empty"},
            {field: "finalUnitLitres", rule: (v: any) => v == null || v > 0 || "FinalUnitLitres is not greater than minimum allowed value: 0"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "OrderLine.VatCodeId cannot be empty"},
            {field: "glCodeId", rule: (v: any) => !v.isEmpty() || "OrderLine.GlCodeId cannot be empty"},
            {field: "eprBomId", rule: (v: any) => v == null || !v.isEmpty() || "OrderLine.EprBomId cannot be empty"},
            {field: "batchUrn", rule: (v: any) => v == null || v.length <= 1000 || "BatchUrn has too many characters (1000)"},
    ];
}


export class OrderRegionalTaxLine extends ModelObject<OrderRegionalTaxLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Order Header Id
    */
    public orderId: Guid = Guid.createEmpty();
    /**
    * Regional Tax Id.
    */
    public regionalTaxId: Guid = Guid.createEmpty();
    /**
    * Tax rate for this regional tax at the time of evaluation. Not user editable.
    */
    public rate: number = 0;
    /**
    * Order total beer litres used to calculate the tax amount. Not user editable.
    */
    public litres: number = 0;
    /**
    * Calculated tax amount for this order and tax rate. Not user editable.
    */
    public taxAmount: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.orderId = Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.regionalTaxId = Guid.parseOrEmpty(jsonSerializedObject.regionalTaxId);
          this.rate = jsonSerializedObject.rate;
          this.litres = jsonSerializedObject.litres;
          this.taxAmount = jsonSerializedObject.taxAmount;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.orderId = this.orderId.toString();
        toRet.regionalTaxId = this.regionalTaxId.toString();
        toRet.rate = this.rate;
        toRet.litres = this.litres;
        toRet.taxAmount = this.taxAmount;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "OrderRegionalTaxLine.Id cannot be empty"},
            {field: "orderId", rule: (v: any) => !v.isEmpty() || "OrderRegionalTaxLine.OrderId cannot be empty"},
            {field: "regionalTaxId", rule: (v: any) => !v.isEmpty() || "OrderRegionalTaxLine.RegionalTaxId cannot be empty"},
    ];
}


export class OrderAdHocBom extends ModelObject<OrderAdHocBom> {

    /**
    * Order this line belongs to.
    */
    public orderId: Guid = Guid.createEmpty();
    /**
    * BOM this line belongs to.
    */
    public eprBomId: Guid = Guid.createEmpty();
    /**
    * Quantity of the adHoc BOM.
    */
    public quantity: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderId = Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.eprBomId = Guid.parseOrEmpty(jsonSerializedObject.eprBomId);
          this.quantity = jsonSerializedObject.quantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderId = this.orderId.toString();
        toRet.eprBomId = this.eprBomId.toString();
        toRet.quantity = this.quantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "orderId", rule: (v: any) => !v.isEmpty() || "OrderAdHocBom.OrderId cannot be empty"},
            {field: "eprBomId", rule: (v: any) => !v.isEmpty() || "OrderAdHocBom.EprBomId cannot be empty"},
    ];
}


export class Order extends ModelObjectWithDirtyCheck<Order> {

    public header: OrderHeader = new OrderHeader();
    public lines: (OrderLine)[] = [];
    public regionalTaxLines: (OrderRegionalTaxLine)[] = [];
    public adHocBoms: (OrderAdHocBom)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new OrderHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new OrderLine(v));
          this.regionalTaxLines = jsonSerializedObject.regionalTaxLines.map((v: any) => new OrderRegionalTaxLine(v));
          this.adHocBoms = jsonSerializedObject.adHocBoms.map((v: any) => new OrderAdHocBom(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        toRet.regionalTaxLines = this.regionalTaxLines.map(v => v.toJsonSerializedObject());
        toRet.adHocBoms = this.adHocBoms.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OrderLine[]).reduce((acc: Array<string>, curr: OrderLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "regionalTaxLines", rule: (v: any) => !!v || "regionalTaxLines is required"},
            {field: "regionalTaxLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OrderRegionalTaxLine[]).reduce((acc: Array<string>, curr: OrderRegionalTaxLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "adHocBoms", rule: (v: any) => !!v || "adHocBoms is required"},
            {field: "adHocBoms", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OrderAdHocBom[]).reduce((acc: Array<string>, curr: OrderAdHocBom) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AdjacentOrderIds extends ModelObject<AdjacentOrderIds> {

    private _previousOrderId: Guid | null = null;
    /**
    * Id of the previous order by order_number for this tenant, if any. All statuses included.
    */
    public get previousOrderId(): Guid | null { return this._previousOrderId }
    public set previousOrderId(value: Guid | null) {
      this._previousOrderId = value === undefined ? null : value;
    }
    private _nextOrderId: Guid | null = null;
    /**
    * Id of the next order by order_number for this tenant, if any. All statuses included.
    */
    public get nextOrderId(): Guid | null { return this._nextOrderId }
    public set nextOrderId(value: Guid | null) {
      this._nextOrderId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.previousOrderId = jsonSerializedObject.previousOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.previousOrderId);
          this.nextOrderId = jsonSerializedObject.nextOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.nextOrderId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.previousOrderId = this.previousOrderId == null ? null : this.previousOrderId.toString();
        toRet.nextOrderId = this.nextOrderId == null ? null : this.nextOrderId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "previousOrderId", rule: (v: any) => v == null || !v.isEmpty() || "AdjacentOrderIds.PreviousOrderId cannot be empty"},
            {field: "nextOrderId", rule: (v: any) => v == null || !v.isEmpty() || "AdjacentOrderIds.NextOrderId cannot be empty"},
    ];
}


export class FilteredOrdersResult extends ModelObject<FilteredOrdersResult> {

    public matchingCount: number = 0;
    public orders: (Order)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.orders = jsonSerializedObject.orders.map((v: any) => new Order(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.orders = this.orders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "orders", rule: (v: any) => !!v || "orders is required"},
            {field: "orders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Order[]).reduce((acc: Array<string>, curr: Order) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AdvancedProductSearchResult extends ModelObject<AdvancedProductSearchResult> {

    public stockItemId: Guid = Guid.createEmpty();
    public stockItemCode: string = "";
    public stockItemName: string = "";
    public listPrice: number = 0;
    private _discountId: Guid | null = null;
    public get discountId(): Guid | null { return this._discountId }
    public set discountId(value: Guid | null) {
      this._discountId = value === undefined ? null : value;
    }
    private _availableQuantity: number | null = null;
    /**
    * Maximum number which can be added without being oversubscribed. Null if not stock tracked
    */
    public get availableQuantity(): number | null { return this._availableQuantity }
    public set availableQuantity(value: number | null) {
      this._availableQuantity = value === undefined ? null : value;
    }
    public isHidden: boolean = false;
    public currencyCode: EnumCurrency = EnumCurrency.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.stockItemCode = jsonSerializedObject.stockItemCode;
          this.stockItemName = jsonSerializedObject.stockItemName;
          this.listPrice = jsonSerializedObject.listPrice;
          this.discountId = jsonSerializedObject.discountId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.discountId);
          this.availableQuantity = jsonSerializedObject.availableQuantity == null ? null : jsonSerializedObject.availableQuantity;
          this.isHidden = jsonSerializedObject.isHidden;
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.stockItemCode = this.stockItemCode;
        toRet.stockItemName = this.stockItemName;
        toRet.listPrice = this.listPrice;
        toRet.discountId = this.discountId == null ? null : this.discountId.toString();
        toRet.availableQuantity = this.availableQuantity == null ? null : this.availableQuantity;
        toRet.isHidden = this.isHidden;
        toRet.currencyCode = this.currencyCode.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AdvancedProductSearchResult.StockItemId cannot be empty"},
            {field: "discountId", rule: (v: any) => v == null || !v.isEmpty() || "AdvancedProductSearchResult.DiscountId cannot be empty"},
    ];
}


export class FindItemsAllowedOnOrderResult extends ModelObject<FindItemsAllowedOnOrderResult> {

    public itemId: Guid = Guid.createEmpty();
    public itemCode: string = "";
    public itemName: string = "";
    public listPrice: number = 0;
    private _discountId: Guid | null = null;
    public get discountId(): Guid | null { return this._discountId }
    public set discountId(value: Guid | null) {
      this._discountId = value === undefined ? null : value;
    }
    private _availableQuantity: number | null = null;
    /**
    * Maximum number which can be added without being oversubscribed. Null if not stock tracked
    */
    public get availableQuantity(): number | null { return this._availableQuantity }
    public set availableQuantity(value: number | null) {
      this._availableQuantity = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.itemCode = jsonSerializedObject.itemCode;
          this.itemName = jsonSerializedObject.itemName;
          this.listPrice = jsonSerializedObject.listPrice;
          this.discountId = jsonSerializedObject.discountId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.discountId);
          this.availableQuantity = jsonSerializedObject.availableQuantity == null ? null : jsonSerializedObject.availableQuantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.itemCode = this.itemCode;
        toRet.itemName = this.itemName;
        toRet.listPrice = this.listPrice;
        toRet.discountId = this.discountId == null ? null : this.discountId.toString();
        toRet.availableQuantity = this.availableQuantity == null ? null : this.availableQuantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "FindItemsAllowedOnOrderResult.ItemId cannot be empty"},
            {field: "discountId", rule: (v: any) => v == null || !v.isEmpty() || "FindItemsAllowedOnOrderResult.DiscountId cannot be empty"},
    ];
}


export class OrderExpectedStockLevelDelta extends ModelObject<OrderExpectedStockLevelDelta> {

    public stockItemId: Guid = Guid.createEmpty();
    public sourceType: EnumStockMovementReferenceSource = EnumStockMovementReferenceSource.none;
    public sourceId: Guid = Guid.createEmpty();
    public stockChangeDate: LocalDate = LocalDate.ofEpochDay(0);
    public stockLevelIncrease: number = 0;
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public urn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.sourceType = EnumStockMovementReferenceSource.getByValue(jsonSerializedObject.sourceType, this.sourceType);
          this.sourceId = Guid.parseOrEmpty(jsonSerializedObject.sourceId);
          this.stockChangeDate = LocalDate.parse(jsonSerializedObject.stockChangeDate);
          this.stockLevelIncrease = jsonSerializedObject.stockLevelIncrease;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.urn = jsonSerializedObject.urn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.sourceType = this.sourceType.value;
        toRet.sourceId = this.sourceId.toString();
        toRet.stockChangeDate = this.stockChangeDate.toString();
        toRet.stockLevelIncrease = this.stockLevelIncrease;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.urn = this.urn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "OrderExpectedStockLevelDelta.StockItemId cannot be empty"},
            {field: "sourceId", rule: (v: any) => !v.isEmpty() || "OrderExpectedStockLevelDelta.SourceId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "OrderExpectedStockLevelDelta.UnitOfMeasureId cannot be empty"},
    ];
}


export class DutySuspendedOrdersResultSet extends ModelObject<DutySuspendedOrdersResultSet> {

    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public dutySuspendedSentLitres: number = 0;
    public dutySuspendedSentLitresOfAlcohol: number = 0;
    public orderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.dutySuspendedSentLitres = jsonSerializedObject.dutySuspendedSentLitres;
          this.dutySuspendedSentLitresOfAlcohol = jsonSerializedObject.dutySuspendedSentLitresOfAlcohol;
          this.orderType = EnumStandardOrder.getByValue(jsonSerializedObject.orderType, this.orderType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.dutySuspendedSentLitres = this.dutySuspendedSentLitres;
        toRet.dutySuspendedSentLitresOfAlcohol = this.dutySuspendedSentLitresOfAlcohol;
        toRet.orderType = this.orderType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PartialOrder extends ModelObject<PartialOrder> {

    public header: OrderHeader = new OrderHeader();
    public lines: (OrderLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new OrderHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new OrderLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as OrderLine[]).reduce((acc: Array<string>, curr: OrderLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class OrderIdBatch extends ModelObject<OrderIdBatch> {

    public orderIds: (Guid)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderIds = jsonSerializedObject.orderIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderIds = this.orderIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class ImportOrderLine extends ModelObject<ImportOrderLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Net (no VAT) Price charged for the line. Not directly editable, calculated from other line properties (List Price, Discount)
    */
    public lineNetPrice: number = 0;
    /**
    * Total VAT for the line. Not directly editable, calculated from other line properties (List Price, Discount)
    */
    public lineVat: number = 0;
    /**
    * Product Name. Stored so invoice can be recreated. Can be edited (useful for eg Miscellaneous / Guest products). Defaults to name of product when line is added to order.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public productName: string = "";
    /**
    * Quantity Ordered
    * Not Negative
    */
    public quantity: number = 0;
    /**
    * Stock Item Id. Given when line is added to order. Not editable.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Stock Location Id from which this line is despatched.
    */
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Unit Net Price. Evaluated from other pricing information. Not editable.
    */
    public unitNetPrice: number = 0;
    /**
    * Vat Percentage at time of order.
    * Not Negative
    */
    public vatPercentage: number = 0;
    /**
    * Vat Code Applicable to the line.
    */
    public vatCodeId: Guid = Guid.createEmpty();
    private _isDesignedForPump: boolean | null = null;
    public get isDesignedForPump(): boolean | null { return this._isDesignedForPump }
    public set isDesignedForPump(value: boolean | null) {
      this._isDesignedForPump = value === undefined ? null : value;
    }
    public currencyPerUnitNetDiscountApplied: number = 0;
    private _bogofId: Guid | null = null;
    /**
    * The Bogof offer, if any, for which this is the free product.
    */
    public get bogofId(): Guid | null { return this._bogofId }
    public set bogofId(value: Guid | null) {
      this._bogofId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.lineNetPrice = jsonSerializedObject.lineNetPrice;
          this.lineVat = jsonSerializedObject.lineVat;
          this.productName = jsonSerializedObject.productName;
          this.quantity = jsonSerializedObject.quantity;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.unitNetPrice = jsonSerializedObject.unitNetPrice;
          this.vatPercentage = jsonSerializedObject.vatPercentage;
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump == null ? null : jsonSerializedObject.isDesignedForPump;
          this.currencyPerUnitNetDiscountApplied = jsonSerializedObject.currencyPerUnitNetDiscountApplied;
          this.bogofId = jsonSerializedObject.bogofId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.bogofId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.lineNetPrice = this.lineNetPrice;
        toRet.lineVat = this.lineVat;
        toRet.productName = this.productName;
        toRet.quantity = this.quantity;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.unitNetPrice = this.unitNetPrice;
        toRet.vatPercentage = this.vatPercentage;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.isDesignedForPump = this.isDesignedForPump == null ? null : this.isDesignedForPump;
        toRet.currencyPerUnitNetDiscountApplied = this.currencyPerUnitNetDiscountApplied;
        toRet.bogofId = this.bogofId == null ? null : this.bogofId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ImportOrderLine.Id cannot be empty"},
            {field: "productName", rule: (v: any) => v == null || v.length !== 0 || "ProductName cannot be empty"},
            {field: "productName", rule: (v: any) => v == null || v.length <= 1000 || "ProductName has too many characters (1000)"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ImportOrderLine.StockItemId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "ImportOrderLine.StockLocationId cannot be empty"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "ImportOrderLine.VatCodeId cannot be empty"},
            {field: "bogofId", rule: (v: any) => v == null || !v.isEmpty() || "ImportOrderLine.BogofId cannot be empty"},
    ];
}


export class SuggestedInvoiceLine extends ModelObject<SuggestedInvoiceLine> {

    public stockItemId: Guid = Guid.createEmpty();
    public productName: string = "";
    public urn: string = "";
    public stockLocationId: Guid = Guid.createEmpty();
    public qtyDelivered: number = 0;
    public listPrice: number = 0;
    private _discount: number | null = null;
    public get discount(): number | null { return this._discount }
    public set discount(value: number | null) {
      this._discount = value === undefined ? null : value;
    }
    public unitPrice: number = 0;
    public lineTotal: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.productName = jsonSerializedObject.productName;
          this.urn = jsonSerializedObject.urn;
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.qtyDelivered = jsonSerializedObject.qtyDelivered;
          this.listPrice = jsonSerializedObject.listPrice;
          this.discount = jsonSerializedObject.discount == null ? null : jsonSerializedObject.discount;
          this.unitPrice = jsonSerializedObject.unitPrice;
          this.lineTotal = jsonSerializedObject.lineTotal;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.productName = this.productName;
        toRet.urn = this.urn;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.qtyDelivered = this.qtyDelivered;
        toRet.listPrice = this.listPrice;
        toRet.discount = this.discount == null ? null : this.discount;
        toRet.unitPrice = this.unitPrice;
        toRet.lineTotal = this.lineTotal;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "SuggestedInvoiceLine.StockItemId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "SuggestedInvoiceLine.StockLocationId cannot be empty"},
    ];
}


export class DuplicateOrderSummary extends ModelObject<DuplicateOrderSummary> {

    /**
    * The order requested for duplication
    */
    public sourceOrderId: Guid = Guid.createEmpty();
    private _targetOrderId: Guid | null = null;
    /**
    * The duplicate order created, if any
    */
    public get targetOrderId(): Guid | null { return this._targetOrderId }
    public set targetOrderId(value: Guid | null) {
      this._targetOrderId = value === undefined ? null : value;
    }
    /**
    * Errors preventing full duplication of the order. Note that errors may be associated with only some lines, in which case duplication of the order will still be possible but will omit the lines showing errors.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errors: (string)[] = [];
    /**
    * Indicates if duplication (which may be full or partial) is possible.  If errors indicates any errors then duplication will only be partial and will omit the lines with errors.
    */
    public isPossible: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sourceOrderId = Guid.parseOrEmpty(jsonSerializedObject.sourceOrderId);
          this.targetOrderId = jsonSerializedObject.targetOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.targetOrderId);
          this.errors = jsonSerializedObject.errors.map((v: any) => v);
          this.isPossible = jsonSerializedObject.isPossible;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sourceOrderId = this.sourceOrderId.toString();
        toRet.targetOrderId = this.targetOrderId == null ? null : this.targetOrderId.toString();
        toRet.errors = this.errors.map(v => v);
        toRet.isPossible = this.isPossible;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sourceOrderId", rule: (v: any) => !v.isEmpty() || "DuplicateOrderSummary.SourceOrderId cannot be empty"},
            {field: "targetOrderId", rule: (v: any) => v == null || !v.isEmpty() || "DuplicateOrderSummary.TargetOrderId cannot be empty"},
    ];
}


export class OutletLastOrderTimestamp extends ModelObject<OutletLastOrderTimestamp> {

    public outletId: Guid = Guid.createEmpty();
    private _latestOrderCreatedAt: Instant | null = null;
    public get latestOrderCreatedAt(): Instant | null { return this._latestOrderCreatedAt }
    public set latestOrderCreatedAt(value: Instant | null) {
      this._latestOrderCreatedAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.latestOrderCreatedAt = jsonSerializedObject.latestOrderCreatedAt == null ? null : Instant.parse(jsonSerializedObject.latestOrderCreatedAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.outletId = this.outletId.toString();
        toRet.latestOrderCreatedAt = this.latestOrderCreatedAt == null ? null : this.latestOrderCreatedAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "OutletLastOrderTimestamp.OutletId cannot be empty"},
    ];
}


export class TopSellingItem extends ModelObject<TopSellingItem> {

    public itemId: Guid = Guid.createEmpty();
    public quantitySold: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.quantitySold = jsonSerializedObject.quantitySold;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.quantitySold = this.quantitySold;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "TopSellingItem.ItemId cannot be empty"},
    ];
}


export class OrderLineStockAvailability extends ModelObject<OrderLineStockAvailability> {

    public orderLineId: Guid = Guid.createEmpty();
    private _availableQuantity: number | null = null;
    public get availableQuantity(): number | null { return this._availableQuantity }
    public set availableQuantity(value: number | null) {
      this._availableQuantity = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderLineId = Guid.parseOrEmpty(jsonSerializedObject.orderLineId);
          this.availableQuantity = jsonSerializedObject.availableQuantity == null ? null : jsonSerializedObject.availableQuantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderLineId = this.orderLineId.toString();
        toRet.availableQuantity = this.availableQuantity == null ? null : this.availableQuantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "orderLineId", rule: (v: any) => !v.isEmpty() || "OrderLineStockAvailability.OrderLineId cannot be empty"},
    ];
}


export class OrderSuggestedStockBatch extends ModelObject<OrderSuggestedStockBatch> {

    public orderId: Guid = Guid.createEmpty();
    public orderLineId: Guid = Guid.createEmpty();
    public batchUrn: string = "";
    public quantity: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.orderId = Guid.parseOrEmpty(jsonSerializedObject.orderId);
          this.orderLineId = Guid.parseOrEmpty(jsonSerializedObject.orderLineId);
          this.batchUrn = jsonSerializedObject.batchUrn;
          this.quantity = jsonSerializedObject.quantity;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.orderId = this.orderId.toString();
        toRet.orderLineId = this.orderLineId.toString();
        toRet.batchUrn = this.batchUrn;
        toRet.quantity = this.quantity;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "orderId", rule: (v: any) => !v.isEmpty() || "OrderSuggestedStockBatch.OrderId cannot be empty"},
            {field: "orderLineId", rule: (v: any) => !v.isEmpty() || "OrderSuggestedStockBatch.OrderLineId cannot be empty"},
    ];
}


export class OrderService extends ServiceBase {

  public async getOrderSummaries(filter: OrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OrderSummariesResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new OrderSummariesResult(response.results);;
    return this.makeJsonRequest("order/v1/getOrderSummaries", req, false, extractor, opts);
  };

  public async getOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/getOrder", req, false, extractor, opts);
  };

  public async getAdjacentOrderIds(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AdjacentOrderIds> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new AdjacentOrderIds(response.adjacentOrderIds);;
    return this.makeJsonRequest("order/v1/getAdjacentOrderIds", req, false, extractor, opts);
  };

  public async getOrders(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Order)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.orders.map((v: any) => new Order(v));;
    return this.makeJsonRequest("order/v1/getOrders", req, false, extractor, opts);
  };

  public async getOrderHeaders(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.orderHeaders.map((v: any) => new OrderHeader(v));;
    return this.makeJsonRequest("order/v1/getOrderHeaders", req, false, extractor, opts);
  };

  public async getOrdersByFilter(filter: OrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredOrdersResult(response.results);;
    return this.makeJsonRequest("order/v1/getOrdersByFilter", req, false, extractor, opts);
  };

  public async createOrder(outletId: Guid, customerReference: string, deliveryDate: LocalDate, despatchDate: LocalDate, dutyReturnDateOverride: LocalDate | null, standardOrderType: EnumStandardOrder, priceListId: Guid, orderSource: EnumOrderSource, externalReference: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    customerReference:customerReference,
    deliveryDate:deliveryDate.toString(),
    despatchDate:despatchDate.toString(),
    dutyReturnDateOverride:dutyReturnDateOverride == null ? null : dutyReturnDateOverride.toString(),
    standardOrderType:standardOrderType.value,
    priceListId:priceListId.toString(),
    orderSource:orderSource.value,
    externalReference:externalReference,
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/createOrder", req, true, extractor, opts);
  };

  public async addLineToOrder(orderId: Guid, orderLineId: Guid, stockLocationId: Guid, stockItemId: Guid, quantity: number, unitOfMeasureId: Guid, urn: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    orderLineId:orderLineId.toString(),
    stockLocationId:stockLocationId.toString(),
    stockItemId:stockItemId.toString(),
    quantity:quantity,
    unitOfMeasureId:unitOfMeasureId.toString(),
    urn:urn == null ? null : urn,
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/addLineToOrder", req, true, extractor, opts);
  };

  public async updateOrderLine(orderId: Guid, orderLineId: Guid, line: OrderLine, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    orderLineId:orderLineId.toString(),
    line:line.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/updateOrderLine", req, true, extractor, opts);
  };

  public async deleteLinesFromOrder(orderHeaderId: Guid, lineIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderHeaderId:orderHeaderId.toString(),
    lineIds:lineIds.map(v => v.toString()),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/deleteLinesFromOrder", req, true, extractor, opts);
  };

  public async buildOrderStandardDeliveryServiceLine(orderHeader: OrderHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OrderLine | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderHeader:orderHeader.toJsonSerializedObject(),
    };
    const extractor = (response:any) => response.deliveryServiceLine == null ? null : new OrderLine(response.deliveryServiceLine);;
    return this.makeJsonRequest("order/v1/buildOrderStandardDeliveryServiceLine", req, false, extractor, opts);
  };

  public async updateOrderDetails(orderHeaderId: Guid, header: OrderHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderHeaderId:orderHeaderId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/updateOrderDetails", req, true, extractor, opts);
  };

  public async assignInvoiceNumbers(orderHeaderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderHeaderIds:orderHeaderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.orderHeaders.map((v: any) => new OrderHeader(v));;
    return this.makeJsonRequest("order/v1/assignInvoiceNumbers", req, true, extractor, opts);
  };

  public async advancedProductSearch2(despatchDate: LocalDate, priceListId: Guid, stockLocationId: Guid, vatCodeId: Guid, outletId: Guid, groupId: Guid | null, standardOrderType: EnumStandardOrder, productBrandId: Guid | null, packageTypeId: Guid | null, supplierId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AdvancedProductSearchResult)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    despatchDate:despatchDate.toString(),
    priceListId:priceListId.toString(),
    stockLocationId:stockLocationId.toString(),
    vatCodeId:vatCodeId.toString(),
    outletId:outletId.toString(),
    groupId:groupId == null ? null : groupId.toString(),
    standardOrderType:standardOrderType.value,
    productBrandId:productBrandId == null ? null : productBrandId.toString(),
    packageTypeId:packageTypeId == null ? null : packageTypeId.toString(),
    supplierId:supplierId == null ? null : supplierId.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new AdvancedProductSearchResult(v));;
    return this.makeJsonRequest("order/v1/advancedProductSearch2", req, false, extractor, opts);
  };

  public async advancedProductSearch(orderId: Guid, stockLocationId: Guid, productBrandId: Guid | null, packageTypeId: Guid | null, supplierId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(AdvancedProductSearchResult)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    stockLocationId:stockLocationId.toString(),
    productBrandId:productBrandId == null ? null : productBrandId.toString(),
    packageTypeId:packageTypeId == null ? null : packageTypeId.toString(),
    supplierId:supplierId == null ? null : supplierId.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new AdvancedProductSearchResult(v));;
    return this.makeJsonRequest("order/v1/advancedProductSearch", req, false, extractor, opts);
  };

  public async cancelOrder(orderId: Guid, cancellationReason: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    cancellationReason:cancellationReason,
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/cancelOrder", req, true, extractor, opts);
  };

  public async completeOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/completeOrder", req, true, extractor, opts);
  };

  public async completeOrders(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/completeOrders", req, true, extractor, opts);
  };

  public async uncompleteOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Order> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new Order(response.order);;
    return this.makeJsonRequest("order/v1/uncompleteOrder", req, true, extractor, opts);
  };

  public async uncompleteOrders(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/uncompleteOrders", req, true, extractor, opts);
  };

  public async archiveOrders(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/archiveOrders", req, true, extractor, opts);
  };

  public async getOrderExpectedStockLevelDeltas(stockLocationId: Guid, stockItemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderExpectedStockLevelDelta)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockLocationId:stockLocationId.toString(),
    stockItemIds:stockItemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new OrderExpectedStockLevelDelta(v));;
    return this.makeJsonRequest("order/v1/getOrderExpectedStockLevelDeltas", req, false, extractor, opts);
  };

  public async evaluateDutySuspendedOrders(fromDutyDateInclusive: LocalDate, toDutyDateInclusive: LocalDate, limitedAlcoholTypes: readonly EnumAlcoholType[] | null, limitedMainPurposes: readonly EnumItemMainPurpose[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutySuspendedOrdersResultSet)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromDutyDateInclusive:fromDutyDateInclusive.toString(),
    toDutyDateInclusive:toDutyDateInclusive.toString(),
    limitedAlcoholTypes:limitedAlcoholTypes == null ? null : limitedAlcoholTypes.map(v => v.value),
    limitedMainPurposes:limitedMainPurposes == null ? null : limitedMainPurposes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedOrdersResultSet(v));;
    return this.makeJsonRequest("order/v1/evaluateDutySuspendedOrders", req, false, extractor, opts);
  };

  public async updateOrderLinesToMatchDelivered(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/updateOrderLinesToMatchDelivered", req, true, extractor, opts);
  };

  public async getSuggestedInvoice(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SuggestedInvoiceLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => response.invoice.map((v: any) => new SuggestedInvoiceLine(v));;
    return this.makeJsonRequest("order/v1/getSuggestedInvoice", req, false, extractor, opts);
  };

  public async removeOrdersOnRetailSummary(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/removeOrdersOnRetailSummary", req, true, extractor, opts);
  };

  public async markOrdersAsDraft(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/markOrdersAsDraft", req, true, extractor, opts);
  };

  public async confirmDraftOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/confirmDraftOrder", req, true, extractor, opts);
  };

  public async markOrdersForPosting(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/markOrdersForPosting", req, true, extractor, opts);
  };

  public async unmarkOrdersForPosting(creditIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    creditIds:creditIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/unmarkOrdersForPosting", req, true, extractor, opts);
  };

  public async recordOrderPostingResult(orderId: Guid, postingError: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    postingError:postingError,
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/recordOrderPostingResult", req, true, extractor, opts);
  };

  public async recordRetailOrderSummaryPostingResult(retailSummaryInvoiceNumber: string, postingError: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    retailSummaryInvoiceNumber:retailSummaryInvoiceNumber,
    postingError:postingError,
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/recordRetailOrderSummaryPostingResult", req, true, extractor, opts);
  };

  public async getDuplicateOrderSummary(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DuplicateOrderSummary> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new DuplicateOrderSummary(response.duplicateOrderSummary);;
    return this.makeJsonRequest("order/v1/getDuplicateOrderSummary", req, false, extractor, opts);
  };

  public async duplicateOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DuplicateOrderSummary> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => new DuplicateOrderSummary(response.duplicateOrderSummary);;
    return this.makeJsonRequest("order/v1/duplicateOrder", req, true, extractor, opts);
  };

  public async updateOrderLineSequence(orderId: Guid, orderLineIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    orderLineIds:orderLineIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/updateOrderLineSequence", req, true, extractor, opts);
  };

  public async getLastOrderTimestamps(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletLastOrderTimestamp)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.lastOrderTimestamps.map((v: any) => new OutletLastOrderTimestamp(v));;
    return this.makeJsonRequest("order/v1/getLastOrderTimestamps", req, false, extractor, opts);
  };

  public async getNextOpenOrdersForOutlets(outletIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderSummary)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletIds:outletIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.orders.map((v: any) => new OrderSummary(v));;
    return this.makeJsonRequest("order/v1/getNextOpenOrdersForOutlets", req, false, extractor, opts);
  };

  public async saveOrderHeader(header: OrderHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/saveOrderHeader", req, true, extractor, opts);
  };

  public async buildOrderHeader(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OrderHeader> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => new OrderHeader(response.header);;
    return this.makeJsonRequest("order/v1/buildOrderHeader", req, false, extractor, opts);
  };

  public async buildOrderLine(orderId: Guid, itemId: Guid, quantity: number, urn: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<OrderLine> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    itemId:itemId.toString(),
    quantity:quantity,
    urn:urn,
    };
    const extractor = (response:any) => new OrderLine(response.line);;
    return this.makeJsonRequest("order/v1/buildOrderLine", req, false, extractor, opts);
  };

  public async findTopSellingProducts(maxResults: number, fromDate: LocalDate, toDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TopSellingItem)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    maxResults:maxResults,
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new TopSellingItem(v));;
    return this.makeJsonRequest("order/v1/findTopSellingProducts", req, false, extractor, opts);
  };

  public async updateTradePortalTransactionFee(orderId: Guid, transactionFee: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    transactionFee:transactionFee,
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/updateTradePortalTransactionFee", req, true, extractor, opts);
  };

  public async updateOrderDetailsToCurrent(orderId: Guid, applyListPrices: boolean, applyGlCodes: boolean, applyVatCodes: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    applyListPrices:applyListPrices,
    applyGlCodes:applyGlCodes,
    applyVatCodes:applyVatCodes,
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/updateOrderDetailsToCurrent", req, true, extractor, opts);
  };

  public async saveOrderAdHocBoms(orderId: Guid, adHocBoms: readonly OrderAdHocBom[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    adHocBoms:adHocBoms.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/saveOrderAdHocBoms", req, false, extractor, opts);
  };

  public async getStockAvailabilityForOrder(orderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderLineStockAvailability)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderId:orderId.toString(),
    };
    const extractor = (response:any) => response.orderLinesStockAvailability.map((v: any) => new OrderLineStockAvailability(v));;
    return this.makeJsonRequest("order/v1/getStockAvailabilityForOrder", req, false, extractor, opts);
  };

  public async getSuggestedOrdersStockBatches(orderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OrderSuggestedStockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    orderIds:orderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.orderSuggestedStockBatch.map((v: any) => new OrderSuggestedStockBatch(v));;
    return this.makeJsonRequest("order/v1/getSuggestedOrdersStockBatches", req, false, extractor, opts);
  };

  public async applySuggestedStockBatchesToOrders(suggestedStockBatches: readonly OrderSuggestedStockBatch[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    suggestedStockBatches:suggestedStockBatches.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("order/v1/applySuggestedStockBatchesToOrders", req, false, extractor, opts);
  };
}

export const orderService = new OrderService();